import { recordPlanningRevisionApplied } from "./planningRevisionDiagnostics.js";
import {
  compareAssignmentsByDisplayedWorkload,
  getDeliveryHighlightKey,
  getAssignmentDisplayedWorkloadMinutes,
  getClientSelectionId,
  getAssignmentTripCount,
  getAssignmentOvertimeState,
  getInitialPreloadHighlightKey,
  getInitialPreloadOrderHighlightKey,
  getLoadingHighlightKey,
  getLoadingOrderHighlightKey,
  getRouteSegmentHighlightKey,
  getSegmentBufferHighlightKey,
  getTripClientItems,
  setAssignmentOpenState,
  syncClientSelectionState,
} from "./resultsFleetData.js";
import { buildExcludedTruckRefreshByTruckId, collectVisibleOrderProtectionSummary } from "./resultsChromeData.js";
import { REORDERED_PROTECTION_TONE } from "./protectionVisualState.js";

const REASSIGNMENT_PLANNING_DIAGNOSTIC_ACTIONS = new Set([
  "end_of_day_load_assignment",
  "end_of_day_load_removal",
  "evening_standard_transfer",
  "gantt_source_backed_save",
  "move_delivery",
  "move_delivery_group",
  "move_truck_orders",
  "optimize_truck_routes",
  "preloaded_previous_day",
  "preloaded_previous_day_assignment",
  "reactivate",
  "remove_preloaded_previous_day",
  "remove_preloaded_previous_day_assignment",
  "segment_reorder",
  "source_changes_remove_modified",
  "suspend",
  "suspend_delivery_group",
]);

function isPlanningDiagnosticsEnabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("planning_diag") === "1";
  } catch {
    return false;
  }
}

export function createPlanningDiagnosticsRecorder() {
  const enabled = isPlanningDiagnosticsEnabled();

  if (enabled && !Array.isArray(window.__planningDiagnosticsRecords)) {
    window.__planningDiagnosticsRecords = [];
  }

  return {
    enabled,
    start(action, targetId, url) {
      const startedAt = performance.now();
      const record = {
        action,
        counters: {},
        enabled,
        targetId,
        url,
        startedAt,
        marks: {},
        server: null,
      };

      record.mark = (name) => {
        record.marks[name] = Number((performance.now() - startedAt).toFixed(1));
      };

      record.increment = (name) => {
        const currentValue = Number(record.counters[name]);
        record.counters[name] = (Number.isFinite(currentValue) ? currentValue : 0) + 1;
      };

      record.attachServerDiagnostics = (diagnostics) => {
        record.server = diagnostics || null;
      };

      record.finish = () => {
        record.totalVisibleMs = Number((performance.now() - startedAt).toFixed(1));
        if (enabled) {
          window.__planningDiagnosticsRecords.push(record);
          console.groupCollapsed(`[planning-diag] ${action} ${targetId || "unknown"} ${record.totalVisibleMs}ms`);
          console.table({
            action: record.action,
            targetId: record.targetId,
            pendingRenderMs: record.marks.pending_render ?? null,
            networkStartMs: record.marks.network_start ?? null,
            responseReceivedMs: record.marks.response_received ?? null,
            responseJsonParsedMs: record.marks.response_json_parsed ?? null,
            resultsLatestFallbackCount: record.counters.results_latest_fallback ?? 0,
            syncStartMs: record.marks.sync_start ?? null,
            syncEndMs: record.marks.sync_end ?? null,
            finalRenderMs: record.marks.final_render ?? null,
            totalVisibleMs: record.totalVisibleMs,
            serverTotalMs: record.server && typeof record.server.total_backend_ms === "number" ? record.server.total_backend_ms : null,
            serverRebuildMs: record.server && record.server.stages_ms && typeof record.server.stages_ms.rebuild_latest_snapshot === "number"
              ? record.server.stages_ms.rebuild_latest_snapshot
              : null,
            reusedRoutes: record.server && record.server.rebuild && typeof record.server.rebuild.reused_route_count === "number"
              ? record.server.rebuild.reused_route_count
              : null,
            recomputedRoutes: record.server && record.server.rebuild && typeof record.server.rebuild.recomputed_route_count === "number"
              ? record.server.rebuild.recomputed_route_count
              : null,
          });

          console.groupEnd();
        }
        return record;
      };

      return record;
    },
  };
}

export function collectRouteTargetElements() {
  return {
    alertBannerRoot: document.querySelector("[data-results-alert-banner-root]"),
    analysisStageRoot: document.querySelector("[data-results-analysis-stage-root]"),
    standardViewRoot: document.querySelector("[data-results-standard-view-root]"),
    detailViewActionsRoot: document.querySelector("[data-results-detail-view-actions-root]"),
    detailViewTabsRoot: document.querySelector("[data-results-detail-view-tabs-root]"),
    dispatchRibbonRoot: document.querySelector("[data-results-dispatch-ribbon-root]"),
    errorsCenterRoot: document.querySelector("[data-results-errors-center-root]"),
    fleetColumnRoot: document.querySelector("[data-results-fleet-column-root]"),
    fleetViewRoot: document.querySelector("[data-results-fleet-view-root]"),
    kpiGridRoot: document.querySelector("[data-results-kpi-grid-root]"),
    mapRoot: document.querySelector("[data-results-route-map-root]"),
    routeHeaderRoot: document.querySelector("[data-results-route-header-root]"),
    routeStepsRoot: document.querySelector("[data-results-route-steps-root]"),
    stageGridRoot: document.querySelector("[data-results-stage-grid]"),
    supportPanelsRoot: document.querySelector("[data-results-support-panels-root]"),
    suspendedPanelRoot: document.querySelector("[data-results-suspended-panel-root]"),
    warningsCenterRoot: document.querySelector("[data-results-warnings-center-root]"),
  };
}

function isPointerTargetWithClosest(target) {
  return Boolean(target && typeof target === "object" && typeof target.closest === "function");
}

export function shouldKeepExpandedTimelineGroupOpenOnPointerDown(target, expandedTimelineGroupKey = "") {
  if (!isPointerTargetWithClosest(target)) {
    return false;
  }

  if (target.closest('[data-fleet-segment-action-popover="true"]')) {
    return true;
  }

  if (target.closest('[data-react-confirmation-modal="true"]')) {
    return true;
  }

  const groupedElement = target.closest("[data-fleet-timeline-group='true'], [data-fleet-timeline-group-member='true']");
  return Boolean(groupedElement && groupedElement.dataset?.groupKey === expandedTimelineGroupKey);
}

function splitAssignmentsByActivity(assignments) {
  const activeAssignments = [];
  const idleAssignments = [];

  if (!Array.isArray(assignments)) {
    return { activeAssignments, idleAssignments };
  }

  assignments.forEach((assignment) => {
    if (getAssignmentTripCount(assignment) > 0) {
      activeAssignments.push(assignment);
      return;
    }

    idleAssignments.push(assignment);
  });

  return { activeAssignments, idleAssignments };
}

function getAssignmentDurationSortValue(assignment) {
  return getAssignmentDisplayedWorkloadMinutes(assignment);
}

function compareAssignmentsByUsage(leftAssignment, rightAssignment) {
  return compareAssignmentsByDisplayedWorkload(leftAssignment, rightAssignment);
}

const PROTECTION_REMOVAL_ACTIONS = new Set([
  "release",
  "release_delivery_group",
  "unconfirm",
  "unconfirm_delivery_group",
  "unconfirm_loading",
  "unconfirm_order",
  "unfreeze",
  "unfreeze_delivery_group",
  "unprotect_truck",
  "unprotect_truck_orders",
]);

function isResultsPayloadSourceStale(resultsData) {
  if (!resultsData || typeof resultsData !== "object") {
    return false;
  }

  const sourceDataState = resultsData.source_data_state && typeof resultsData.source_data_state === "object"
    ? resultsData.source_data_state
    : null;
  const sourceStatus = normalizeInlineText(sourceDataState?.status).toLowerCase();

  return sourceDataState?.stale === true
    || sourceStatus === "stale"
    || resultsData.is_operationally_current === false;
}

export function preserveOperationalFreshnessForProtectionRemoval(currentResultsData, nextResultsData, actionName = "") {
  if (!nextResultsData || typeof nextResultsData !== "object") {
    return nextResultsData;
  }

  if (!PROTECTION_REMOVAL_ACTIONS.has(normalizeInlineText(actionName).toLowerCase())) {
    return nextResultsData;
  }

  if (isResultsPayloadSourceStale(currentResultsData) || !isResultsPayloadSourceStale(nextResultsData)) {
    return nextResultsData;
  }

  const mergedResultsData = {
    ...nextResultsData,
    is_operationally_current: true,
  };

  if (currentResultsData?.source_data_state && typeof currentResultsData.source_data_state === "object") {
    mergedResultsData.source_data_state = { ...currentResultsData.source_data_state };
  } else {
    delete mergedResultsData.source_data_state;
  }

  if (nextResultsData.export_context && typeof nextResultsData.export_context === "object") {
    const currentSnapshotStatus = normalizeInlineText(currentResultsData?.export_context?.snapshot_status).toLowerCase();
    if (currentSnapshotStatus === "ready") {
      mergedResultsData.export_context = {
        ...nextResultsData.export_context,
        snapshot_status: "ready",
      };
    }
  }

  if (normalizeInlineText(nextResultsData.code).toLowerCase() === "latest_optimization_stale") {
    delete mergedResultsData.code;
    delete mergedResultsData.error;
    delete mergedResultsData.resource;
  }

  return mergedResultsData;
}

function sortAssignmentsByUsage(assignments) {
  return (Array.isArray(assignments) ? [...assignments] : []).sort(compareAssignmentsByUsage);
}

function getSelectionDurationMinutes(assignment) {
  return getAssignmentDisplayedWorkloadMinutes(assignment);
}

function getPreferredAssignmentTrip(assignment) {
  const trips = Array.isArray(assignment?.trips) ? assignment.trips.filter(Boolean) : [];
  if (!trips.length) {
    return null;
  }

  return trips.find((trip) => trip?.route?.geometry_available) || trips[0] || null;
}

export function resolveMostOccupiedAssignmentEntry(assignments) {
  const assignmentList = Array.isArray(assignments) ? assignments.filter(Boolean) : [];
  let selectedAssignment = null;
  let selectedDurationMinutes = -1;
  let selectedTripCount = -1;

  assignmentList.forEach((assignment) => {
    const tripCount = getAssignmentTripCount(assignment);
    if (tripCount <= 0) {
      return;
    }

    const durationMinutes = getSelectionDurationMinutes(assignment);
    if (
      durationMinutes > selectedDurationMinutes
      || (durationMinutes === selectedDurationMinutes && tripCount > selectedTripCount)
    ) {
      selectedAssignment = assignment;
      selectedDurationMinutes = durationMinutes;
      selectedTripCount = tripCount;
    }
  });

  if (!selectedAssignment) {
    return null;
  }

  return {
    assignment: selectedAssignment,
    trip: getPreferredAssignmentTrip(selectedAssignment),
  };
}

function getDeliveryDisplayKey(item) {
  if (typeof item?.delivery_id === "string" && item.delivery_id.trim()) {
    return item.delivery_id.trim();
  }

  if (typeof item?.order_id === "string" && item.order_id.trim()) {
    return item.order_id.trim();
  }

  return "";
}

function bumpWorkspaceRevision(state) {
  if (!state) {
    return 0;
  }

  const nextRevision = (Number(state.workspaceRevision) || 0) + 1;
  state.workspaceRevision = nextRevision;
  return nextRevision;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function resolveReassignmentPlanningDiagnostics(resultsData) {
  if (!isPlainObject(resultsData)) {
    return null;
  }

  const candidates = [
    resultsData._planning_diagnostics,
    resultsData.planning_diagnostics,
    resultsData.last_reassignment_diagnostics,
    resultsData.lastReassignmentDiagnostics,
    resultsData.diagnostics,
  ];

  for (const candidate of candidates) {
    if (!isPlainObject(candidate)) {
      continue;
    }

    const action = normalizeInlineText(candidate.action).toLowerCase();
    if (REASSIGNMENT_PLANNING_DIAGNOSTIC_ACTIONS.has(action)) {
      return candidate;
    }
  }

  return null;
}

function clonePlainPayload(value) {
  if (!isPlainObject(value)) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return { ...value };
  }
}

function resolveResultsDispatchDate(resultsData) {
  return normalizeInlineText(resultsData?.dispatch_date)
    || normalizeInlineText(resultsData?.planning_summary?.dispatch_date);
}

function resolveDiagnosticRecordedAtEpochMs(diagnostics) {
  const rawValue = normalizeInlineText(diagnostics?.recorded_at);
  if (!rawValue) {
    return null;
  }

  const epochMs = Date.parse(rawValue);
  return Number.isFinite(epochMs) ? epochMs : null;
}

function hasDirectReassignmentPlanningDiagnostics(resultsData) {
  return [resultsData?._planning_diagnostics, resultsData?.planning_diagnostics].some((candidate) => {
    if (!isPlainObject(candidate)) {
      return false;
    }
    return REASSIGNMENT_PLANNING_DIAGNOSTIC_ACTIONS.has(
      normalizeInlineText(candidate.action).toLowerCase(),
    );
  });
}

function shouldKeepCurrentReassignmentDiagnostic(
  state,
  diagnostics,
  dispatchDate,
  { isDirectCandidate = false } = {},
) {
  const currentPayload = isPlainObject(state?.lastReassignmentDiagnosticPayload)
    ? state.lastReassignmentDiagnosticPayload
    : null;
  const currentDiagnostics = resolveReassignmentPlanningDiagnostics(currentPayload);
  if (!currentDiagnostics) {
    return false;
  }

  const currentDispatchDate = resolveResultsDispatchDate(currentPayload);
  if (dispatchDate && currentDispatchDate && dispatchDate !== currentDispatchDate) {
    return false;
  }

  const currentRecordedAt = resolveDiagnosticRecordedAtEpochMs(currentDiagnostics);
  if (currentRecordedAt === null) {
    return false;
  }

  const candidateRecordedAt = resolveDiagnosticRecordedAtEpochMs(diagnostics);
  if (candidateRecordedAt === null) {
    return !isDirectCandidate;
  }

  return candidateRecordedAt <= currentRecordedAt;
}

export function captureLastReassignmentDiagnosticPayload(state, resultsData) {
  if (!state) {
    return null;
  }

  const diagnostics = resolveReassignmentPlanningDiagnostics(resultsData);
  if (!diagnostics) {
    return null;
  }

  const diagnosticsCopy = clonePlainPayload(diagnostics);
  if (!diagnosticsCopy) {
    return null;
  }

  const dispatchDate = resolveResultsDispatchDate(resultsData);
  const isDirectCandidate = hasDirectReassignmentPlanningDiagnostics(resultsData);
  if (shouldKeepCurrentReassignmentDiagnostic(
    state,
    diagnosticsCopy,
    dispatchDate,
    { isDirectCandidate },
  )) {
    return state.lastReassignmentDiagnosticPayload;
  }

  state.lastReassignmentDiagnosticPayload = {
    _planning_diagnostics: diagnosticsCopy,
    captured_at: new Date().toISOString(),
    dispatch_date: dispatchDate,
    planning_summary: dispatchDate ? { dispatch_date: dispatchDate } : {},
  };
  return state.lastReassignmentDiagnosticPayload;
}

export function buildResultsState(resultsData, { previousState = null } = {}) {
  const dispatchDate = resolveResultsDispatchDate(resultsData);
  const previousDiagnosticPayload = isPlainObject(previousState?.lastReassignmentDiagnosticPayload)
    ? previousState.lastReassignmentDiagnosticPayload
    : null;
  const previousDiagnosticDispatchDate = resolveResultsDispatchDate(previousDiagnosticPayload);
  const preservedDiagnosticPayload = (
    dispatchDate
    && previousDiagnosticDispatchDate === dispatchDate
  )
    ? clonePlainPayload(previousDiagnosticPayload)
    : null;
  const state = {
    resultsData,
    assignmentList: [],
    assignmentsById: new Map(),
    tripsById: new Map(),
    deliveryItemsByOrderId: new Map(),
    planningSummary: {},
    pendingGlobalPlanningMutationCount: 0,
    pendingMutationByOrderId: new Set(),
    pendingLoadingMutationById: new Set(),
    pendingDeliveryMutationById: new Set(),
    currentAssignment: null,
    currentTrip: null,
    currentMapRenderMeta: null,
    selectedClientId: null,
    openOperationalReassignmentKey: null,
    transientActivatedDispatchPeriod: "",
    transientActivatedHighlightSurface: "",
    transientActivatedStepKeys: new Set(),
    clientEntriesById: new Map(),
    openTruckIds: new Set(),
    renderFleetColumn: null,
    lastReassignmentDiagnosticPayload: preservedDiagnosticPayload,
    workspaceRevision: 0,
  };

  applyResultsDataToState(state, resultsData);
  return state;
}

export function hasPendingPlanningMutation(
  state,
  { orderId = null, loadingStepId = null, deliveryId = null } = {},
) {
  if (!state) {
    return false;
  }

  const pendingGlobalPlanningMutationCount = Number(state.pendingGlobalPlanningMutationCount) || 0;

  return Boolean(
    pendingGlobalPlanningMutationCount > 0
    || (orderId && state.pendingMutationByOrderId instanceof Set && state.pendingMutationByOrderId.has(orderId))
    || (loadingStepId && state.pendingLoadingMutationById instanceof Set && state.pendingLoadingMutationById.has(loadingStepId))
    || (deliveryId && state.pendingDeliveryMutationById instanceof Set && state.pendingDeliveryMutationById.has(deliveryId)),
  );
}

export function addPendingPlanningMutation(
  state,
  { orderId = null, loadingStepId = null, deliveryId = null } = {},
) {
  if (!state) {
    return state;
  }

  if (!(state.pendingMutationByOrderId instanceof Set)) {
    state.pendingMutationByOrderId = new Set();
  }
  if (!(state.pendingLoadingMutationById instanceof Set)) {
    state.pendingLoadingMutationById = new Set();
  }
  if (!(state.pendingDeliveryMutationById instanceof Set)) {
    state.pendingDeliveryMutationById = new Set();
  }

  const hasScopedMutation = Boolean(orderId || loadingStepId || deliveryId);
  if (!hasScopedMutation) {
    state.pendingGlobalPlanningMutationCount = Math.max(0, (Number(state.pendingGlobalPlanningMutationCount) || 0)) + 1;
  }
  if (orderId) {
    state.pendingMutationByOrderId.add(orderId);
  }
  if (loadingStepId) {
    state.pendingLoadingMutationById.add(loadingStepId);
  }
  if (deliveryId) {
    state.pendingDeliveryMutationById.add(deliveryId);
  }

  bumpWorkspaceRevision(state);
  return state;
}

export function clearPendingPlanningMutation(
  state,
  { orderId = null, loadingStepId = null, deliveryId = null } = {},
) {
  if (!state) {
    return state;
  }

  if (orderId && state.pendingMutationByOrderId instanceof Set) {
    state.pendingMutationByOrderId.delete(orderId);
  }
  if (loadingStepId && state.pendingLoadingMutationById instanceof Set) {
    state.pendingLoadingMutationById.delete(loadingStepId);
  }
  if (deliveryId && state.pendingDeliveryMutationById instanceof Set) {
    state.pendingDeliveryMutationById.delete(deliveryId);
  }
  if (!orderId && !loadingStepId && !deliveryId) {
    state.pendingGlobalPlanningMutationCount = Math.max(0, (Number(state.pendingGlobalPlanningMutationCount) || 0) - 1);
  }

  return state;
}

export function applyResultsDataToState(state, resultsData, { actionName = "results_latest" } = {}) {
  state.lastPlanningOperation = actionName;
  const assignmentList = sortAssignmentsByUsage(resultsData?.assignments);
  const assignmentsById = new Map();
  const tripsById = new Map();
  const deliveryItemsByOrderId = new Map();
  const clientEntriesById = new Map();
  const previousOpenTruckIds = state.openTruckIds instanceof Set ? state.openTruckIds : null;
  let firstTripEntry = null;
  let firstRenderableTripEntry = null;

  assignmentList.forEach((assignment) => {
    assignmentsById.set(assignment.truck_id, assignment);

    (assignment.trips || []).forEach((trip) => {
      const entry = { assignment, trip };
      tripsById.set(trip.trip_id, entry);

      getTripClientItems(trip).forEach((client) => {
        const clientId = getClientSelectionId(client);
        if (clientId && !clientEntriesById.has(clientId)) {
          clientEntriesById.set(clientId, {
            assignmentId: assignment.truck_id,
            tripId: trip.trip_id,
          });
        }
      });

      (trip.operational_steps || []).forEach((step) => {
        const clientId = getClientSelectionId(step);
        if (clientId && !clientEntriesById.has(clientId)) {
          clientEntriesById.set(clientId, {
            assignmentId: assignment.truck_id,
            tripId: trip.trip_id,
          });
        }
      });

      if (!firstTripEntry) {
        firstTripEntry = entry;
      }

      if (!firstRenderableTripEntry && trip.route && trip.route.geometry_available) {
        firstRenderableTripEntry = entry;
      }
    });

    (assignment.delivery_items || []).forEach((item) => {
      const itemKey = getDeliveryDisplayKey(item);
      if (itemKey) {
        deliveryItemsByOrderId.set(itemKey, item);
      }
    });
  });

  captureLastReassignmentDiagnosticPayload(state, resultsData);

  if (typeof state.workspaceRevision === "number") {
    recordPlanningRevisionApplied(resultsData, actionName, state.resultsData?.runtime_revision);
  }
  state.resultsData = resultsData;
  state.assignmentList = assignmentList;
  state.assignmentsById = assignmentsById;
  state.tripsById = tripsById;
  state.deliveryItemsByOrderId = deliveryItemsByOrderId;
  state.clientEntriesById = clientEntriesById;
  state.openTruckIds = new Set(
    assignmentList
      .map((assignment) => assignment.truck_id)
      .filter((assignmentId) => !previousOpenTruckIds || previousOpenTruckIds.has(assignmentId)),
  );
  state.planningSummary = resultsData && typeof resultsData.planning_summary === "object"
    ? resultsData.planning_summary
    : {};

  if (state.selectedClientId && !clientEntriesById.has(state.selectedClientId)) {
    state.selectedClientId = null;
  }

  state.firstTripEntry = firstRenderableTripEntry || firstTripEntry;
  bumpWorkspaceRevision(state);
  return state;
}

export function toggleClientSelection(state, {
  assignmentId = null,
  clientId,
  openSelectionOnly = false,
  tripId = null,
} = {}) {
  if (!state || !clientId) {
    return {
      nextAssignment: state?.currentAssignment || null,
      nextTrip: state?.currentTrip || null,
      selectionChanged: false,
    };
  }

  if (state.selectedClientId === clientId) {
    state.selectedClientId = null;
    syncClientSelectionState(state);
    return {
      nextAssignment: state.currentAssignment || null,
      nextTrip: state.currentTrip || null,
      selectionChanged: false,
    };
  }

  state.selectedClientId = clientId;
  const selectionEntry = state.clientEntriesById.get(clientId) || {
    assignmentId,
    tripId,
  };

  if (selectionEntry.assignmentId) {
    if (openSelectionOnly) {
      state.openTruckIds = new Set([selectionEntry.assignmentId]);
    } else {
      setAssignmentOpenState(state, selectionEntry.assignmentId, true);
    }
  }

  const nextAssignment = selectionEntry.assignmentId
    ? state.assignmentsById.get(selectionEntry.assignmentId) || null
    : state.currentAssignment || null;
  const nextTrip = selectionEntry.tripId
    ? (state.tripsById.get(selectionEntry.tripId) || {}).trip || null
    : state.currentTrip || null;
  const assignmentChanged = Boolean(nextAssignment) && state.currentAssignment?.truck_id !== nextAssignment.truck_id;
  const tripChanged = (nextTrip?.trip_id || null) !== (state.currentTrip?.trip_id || null);

  syncClientSelectionState(state);

  return {
    nextAssignment,
    nextTrip,
    selectionChanged: Boolean(nextAssignment) && (assignmentChanged || tripChanged),
  };
}

export function revealAssignmentCard(state, assignmentId) {
  if (!assignmentId) {
    return;
  }

  setAssignmentOpenState(state, assignmentId, true);

  if (typeof state?.renderFleetColumn === "function") {
    state.renderFleetColumn();
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const escapedAssignmentId = window.CSS?.escape ? window.CSS.escape(assignmentId) : assignmentId;
      const selector = `[data-assignment-card][data-assignment-id="${escapedAssignmentId}"]`;
      const card = document.querySelector(selector);

      if (card instanceof HTMLElement) {
        card.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
    });
  });
}

function collectLoadingLinkedDeliveryStates(resultsData, loadingStepId) {
  if (!loadingStepId) {
    return [];
  }

  const entries = [];

  for (const assignment of resultsData?.assignments || []) {
    for (const trip of assignment?.trips || []) {
      for (const client of trip?.clients || []) {
        if (!client || client.loading_step_id !== loadingStepId || !client.delivery_id) {
          continue;
        }

        entries.push({
          assignmentId: assignment?.truck_id || null,
          deliveryId: client.delivery_id,
          highlightKey: getDeliveryHighlightKey(client.delivery_id),
          isBlocked: Boolean(client.is_delivery_blocked || client.blocking_reason),
          tripId: trip?.trip_id || null,
        });
      }
    }
  }

  return entries;
}

export function resolveLoadingActivationTransition(previousResultsData, nextResultsData, loadingStepId) {
  const previousEntries = collectLoadingLinkedDeliveryStates(previousResultsData, loadingStepId);
  const nextEntries = collectLoadingLinkedDeliveryStates(nextResultsData, loadingStepId);
  const previousByDeliveryId = new Map(previousEntries.map((entry) => [entry.deliveryId, entry]));
  const activatedEntries = nextEntries.filter((entry) => {
    const previousEntry = previousByDeliveryId.get(entry.deliveryId);
    return Boolean(previousEntry?.isBlocked) && !entry.isBlocked;
  });
  const fallbackEntries = activatedEntries.length > 0 ? activatedEntries : nextEntries;

  return {
    highlightedStepKeys: fallbackEntries
      .map((entry) => entry.highlightKey)
      .filter((highlightKey) => typeof highlightKey === "string" && highlightKey),
    preferredAssignmentId: fallbackEntries[0]?.assignmentId || null,
    preferredDeliveryId: fallbackEntries[0]?.deliveryId || null,
    preferredTripId: fallbackEntries[0]?.tripId || null,
  };
}

function findDeliveryLinkedLoadingEntry(resultsData, deliveryId) {
  if (!deliveryId) {
    return null;
  }

  for (const assignment of resultsData?.assignments || []) {
    for (const trip of assignment?.trips || []) {
      for (const client of trip?.clients || []) {
        if (!client || client.delivery_id !== deliveryId) {
          continue;
        }

        const highlightedStepKeys = [
          client.loading_step_id ? getLoadingHighlightKey(client.loading_step_id) : "",
          client.order_id ? getLoadingOrderHighlightKey(client.order_id) : "",
        ].filter(Boolean);
        if (highlightedStepKeys.length === 0) {
          continue;
        }

        return {
          assignmentId: assignment?.truck_id || null,
          deliveryId,
          highlightedStepKeys,
          tripId: trip?.trip_id || null,
        };
      }
    }

    const deliveryItem = (Array.isArray(assignment?.delivery_items) ? assignment.delivery_items : [])
      .find((item) => item?.delivery_id === deliveryId && item?.order_id);
    if (deliveryItem?.order_id) {
      return {
        assignmentId: assignment?.truck_id || null,
        deliveryId,
        highlightedStepKeys: [getLoadingOrderHighlightKey(deliveryItem.order_id)],
        tripId: null,
      };
    }
  }

  return null;
}

export function resolveDeliveryLinkedLoadingTransition(previousResultsData, nextResultsData, deliveryId) {
  const linkedEntry = findDeliveryLinkedLoadingEntry(nextResultsData, deliveryId)
    || findDeliveryLinkedLoadingEntry(previousResultsData, deliveryId);

  return {
    highlightedStepKeys: Array.isArray(linkedEntry?.highlightedStepKeys)
      ? linkedEntry.highlightedStepKeys
      : [],
    preferredAssignmentId: linkedEntry?.assignmentId || null,
    preferredDeliveryId: linkedEntry?.deliveryId || null,
    preferredTripId: linkedEntry?.tripId || null,
  };
}

function normalizeDeliveryHighlightIds(deliveryIds = []) {
  return (Array.isArray(deliveryIds) ? deliveryIds : [])
    .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
    .map((deliveryId) => deliveryId.trim())
    .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
}

export function resolveDeliveryGroupLinkedLoadingTransition(
  previousResultsData,
  nextResultsData,
  deliveryIds = [],
  { stableOrderKeysOnly = false } = {},
) {
  const resolvedDeliveryIds = normalizeDeliveryHighlightIds(deliveryIds);
  const highlightedStepKeys = [];
  let preferredAssignmentId = null;
  let preferredDeliveryId = null;
  let preferredTripId = null;

  resolvedDeliveryIds.forEach((deliveryId) => {
    const transition = resolveDeliveryLinkedLoadingTransition(previousResultsData, nextResultsData, deliveryId);
    if (!preferredAssignmentId && transition.preferredAssignmentId) {
      preferredAssignmentId = transition.preferredAssignmentId;
      preferredDeliveryId = transition.preferredDeliveryId;
      preferredTripId = transition.preferredTripId;
    }
    highlightedStepKeys.push(...transition.highlightedStepKeys);
  });

  return {
    highlightedStepKeys: highlightedStepKeys.filter((highlightKey, index, items) => (
      typeof highlightKey === "string"
      && highlightKey
      && (!stableOrderKeysOnly || highlightKey.startsWith("loading-order:"))
      && items.indexOf(highlightKey) === index
    )),
    preferredAssignmentId,
    preferredDeliveryId,
    preferredTripId,
  };
}

const SINGLE_DELIVERY_MUTATION_HIGHLIGHT_ACTIONS = new Set([
  "confirm",
  "freeze",
  "move_delivery",
  "reactivate",
  "release",
  "suspend",
  "suspend_from_error",
  "unconfirm",
  "unfreeze",
]);

const GROUP_DELIVERY_MUTATION_HIGHLIGHT_ACTIONS = new Set([
  "end_of_day_load_assignment",
  "evening_standard_transfer",
  "move_delivery_group",
  "release_delivery_group",
  "suspend_delivery_group",
  "unconfirm_delivery_group",
]);

const LOADING_MUTATION_HIGHLIGHT_ACTIONS = new Set([
  "confirm_loading",
  "unconfirm_loading",
]);

const PRELOADED_PREVIOUS_DAY_ASSIGNMENT_HIGHLIGHT_ACTIONS = new Set([
  "preloaded_previous_day_assignment",
]);

const PRELOADED_PREVIOUS_DAY_REMOVAL_HIGHLIGHT_ACTIONS = new Set([
  "remove_preloaded_previous_day_assignment",
]);

const ROUTE_SEGMENT_MUTATION_HIGHLIGHT_ACTIONS = new Set([
  "route_alternative_apply",
  "route_alternative_remove",
]);

const TRUCK_OPERATIONAL_STEP_MUTATION_HIGHLIGHT_ACTIONS = new Set([
  "disable_truck_dompeur_mixed_load",
  "enable_truck_dompeur_mixed_load",
]);

const TARGETED_DELIVERY_LINKED_LOADING_HIGHLIGHT_ACTIONS = new Set([
  "move_delivery",
  "reactivate",
]);

const DELIVERY_MOVE_HIGHLIGHT_ACTIONS = new Set([
  "move_delivery",
  "move_delivery_group",
]);

function normalizePlanningHighlightValue(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return "";
}

function normalizePlanningHighlightValues(values) {
  return (Array.isArray(values) ? values : [values])
    .map((value) => normalizePlanningHighlightValue(value))
    .filter((value, index, items) => value && items.indexOf(value) === index);
}

function appendPlanningHighlightKey(highlightKeys, highlightKey) {
  const safeHighlightKey = normalizePlanningHighlightValue(highlightKey);
  if (safeHighlightKey && !highlightKeys.includes(safeHighlightKey)) {
    highlightKeys.push(safeHighlightKey);
  }
}

function appendDeliveryHighlightKeys(highlightKeys, deliveryIds) {
  normalizePlanningHighlightValues(deliveryIds).forEach((item) => {
    appendPlanningHighlightKey(highlightKeys, getDeliveryHighlightKey(item));
  });
}

function appendLoadingHighlightKeys(highlightKeys, loadingStepIds) {
  normalizePlanningHighlightValues(loadingStepIds).forEach((item) => {
    appendPlanningHighlightKey(highlightKeys, getLoadingHighlightKey(item));
  });
}

function appendLoadingOrderHighlightKeys(highlightKeys, orderIds) {
  normalizePlanningHighlightValues(orderIds).forEach((item) => {
    appendPlanningHighlightKey(highlightKeys, getLoadingOrderHighlightKey(item));
  });
}

function appendInitialPreloadHighlightKeys(highlightKeys, loadingStepIds) {
  normalizePlanningHighlightValues(loadingStepIds).forEach((item) => {
    appendPlanningHighlightKey(highlightKeys, getInitialPreloadHighlightKey(item));
  });
}

function appendInitialPreloadOrderHighlightKeys(highlightKeys, orderIds) {
  normalizePlanningHighlightValues(orderIds).forEach((item) => {
    appendPlanningHighlightKey(highlightKeys, getInitialPreloadOrderHighlightKey(item));
  });
}

function appendResultInitialPreloadHighlightKeys(highlightKeys, resultPayload, {
  assignmentId = "",
  deliveryIds = [],
  orderIds = [],
} = {}) {
  if (!resultPayload || typeof resultPayload !== "object") {
    return;
  }

  const safeAssignmentId = normalizePlanningHighlightValue(assignmentId);
  const requestedDeliveryIds = new Set(normalizePlanningHighlightValues(deliveryIds));
  const requestedOrderIds = new Set(normalizePlanningHighlightValues(orderIds));
  if (requestedDeliveryIds.size === 0 && requestedOrderIds.size === 0) {
    return;
  }

  (Array.isArray(resultPayload.assignments) ? resultPayload.assignments : [])
    .filter((assignment) => (
      !safeAssignmentId
      || normalizePlanningHighlightValue(assignment?.truck_id) === safeAssignmentId
    ))
    .forEach((assignment) => {
      const operationalSteps = [
        ...(Array.isArray(assignment?.operational_steps) ? assignment.operational_steps : []),
        ...(Array.isArray(assignment?.trips)
          ? assignment.trips.flatMap((trip) => (
              Array.isArray(trip?.operational_steps) ? trip.operational_steps : []
            ))
          : []),
      ];

      operationalSteps.forEach((rawStep) => {
        const step = rawStep && typeof rawStep === "object" ? rawStep : null;
        const stepType = normalizePlanningHighlightValue(step?.step_type).toLowerCase();
        if (!step || stepType !== "loading" || step.is_initial_preload !== true) {
          return;
        }

        const stepDeliveryIds = normalizePlanningHighlightValues([
          step.delivery_id,
          ...(Array.isArray(step.delivery_ids) ? step.delivery_ids : []),
        ]);
        const stepOrderIds = normalizePlanningHighlightValues([
          step.order_id,
          ...(Array.isArray(step.order_ids) ? step.order_ids : []),
        ]);
        const matchesRequestedDelivery = stepDeliveryIds.some((deliveryId) => requestedDeliveryIds.has(deliveryId));
        const matchesRequestedOrder = stepOrderIds.some((orderId) => requestedOrderIds.has(orderId));
        if (!matchesRequestedDelivery && !matchesRequestedOrder) {
          return;
        }

        appendInitialPreloadHighlightKeys(highlightKeys, [step.step_id]);
        appendInitialPreloadOrderHighlightKeys(highlightKeys, stepOrderIds);
      });
    });
}

function appendTransitionHighlightKeys(highlightKeys, transition) {
  (Array.isArray(transition?.highlightedStepKeys) ? transition.highlightedStepKeys : []).forEach((highlightKey) => {
    appendPlanningHighlightKey(highlightKeys, highlightKey);
  });
}

function resolveTransitionLoadingOrderIds(transition) {
  return normalizePlanningHighlightValues(
    (Array.isArray(transition?.highlightedStepKeys) ? transition.highlightedStepKeys : [])
      .filter((highlightKey) => (
        typeof highlightKey === "string"
        && highlightKey.startsWith("loading-order:")
      ))
      .map((highlightKey) => highlightKey.slice("loading-order:".length)),
  );
}

function appendRouteSegmentHighlightKeys(highlightKeys, tripId, routeSegmentIndex, routeSegmentIndexes = []) {
  const safeTripId = normalizePlanningHighlightValue(tripId);
  if (!safeTripId) {
    return;
  }

  normalizePlanningHighlightValues([
    routeSegmentIndex,
    ...(Array.isArray(routeSegmentIndexes) ? routeSegmentIndexes : []),
  ]).forEach((item) => {
    appendPlanningHighlightKey(highlightKeys, getRouteSegmentHighlightKey(safeTripId, item));
  });
}

function appendOperationalStepHighlightKeys(highlightKeys, rawStep) {
  const step = rawStep && typeof rawStep === "object" ? rawStep : null;
  if (!step) {
    return;
  }

  const stepType = normalizePlanningHighlightValue(step.step_type).toLowerCase();
  if (stepType === "loading") {
    appendLoadingHighlightKeys(highlightKeys, [step.step_id]);
    appendLoadingOrderHighlightKeys(highlightKeys, [
      step.order_id,
      ...(Array.isArray(step.order_ids) ? step.order_ids : []),
    ]);
    return;
  }

  if (stepType === "delivery") {
    appendDeliveryHighlightKeys(highlightKeys, [
      step.delivery_id,
      ...(Array.isArray(step.delivery_ids) ? step.delivery_ids : []),
    ]);
  }
}

function appendTruckOperationalStepHighlightKeys(highlightKeys, resultPayload, truckId) {
  const safeTruckId = normalizePlanningHighlightValue(truckId);
  if (!safeTruckId || !resultPayload || typeof resultPayload !== "object") {
    return;
  }

  const assignment = (Array.isArray(resultPayload.assignments) ? resultPayload.assignments : [])
    .find((item) => normalizePlanningHighlightValue(item?.truck_id) === safeTruckId);
  if (!assignment || typeof assignment !== "object") {
    return;
  }

  (Array.isArray(assignment.operational_steps) ? assignment.operational_steps : [])
    .forEach((step) => appendOperationalStepHighlightKeys(highlightKeys, step));
  (Array.isArray(assignment.trips) ? assignment.trips : [])
    .forEach((trip) => {
      (Array.isArray(trip?.operational_steps) ? trip.operational_steps : [])
        .forEach((step) => appendOperationalStepHighlightKeys(highlightKeys, step));
    });
}

export function resolvePlanningMutationHighlightKeys({
  actionName = "",
  assignmentId = "",
  body = null,
  deliveryId = "",
  deliveryIds = [],
  linkedStepTransition = null,
  deliveryTransition = null,
  loadingStepId = "",
  orderId = "",
  resultPayload = null,
} = {}) {
  const safeActionName = normalizePlanningHighlightValue(actionName);
  const mutationBody = body && typeof body === "object" ? body : {};
  const highlightKeys = [];

  if (SINGLE_DELIVERY_MUTATION_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendDeliveryHighlightKeys(highlightKeys, [
      deliveryId,
      mutationBody.delivery_id,
    ]);
  }

  if (GROUP_DELIVERY_MUTATION_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendDeliveryHighlightKeys(highlightKeys, [
      deliveryId,
      ...normalizeDeliveryHighlightIds(deliveryIds),
      ...(Array.isArray(mutationBody.delivery_ids) ? mutationBody.delivery_ids : []),
    ]);
  }

  if (LOADING_MUTATION_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendLoadingHighlightKeys(highlightKeys, [
      loadingStepId,
      mutationBody.loading_step_id,
      mutationBody.step_id,
    ]);
  }

  if (PRELOADED_PREVIOUS_DAY_ASSIGNMENT_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    const preloadedPreviousDayLoadingStepIds = normalizePlanningHighlightValues([
      ...(Array.isArray(mutationBody.group_step_ids) ? mutationBody.group_step_ids : []),
      ...(Array.isArray(mutationBody.loading_step_ids) ? mutationBody.loading_step_ids : []),
      ...(Array.isArray(mutationBody.step_ids) ? mutationBody.step_ids : []),
    ]);
    const preloadedPreviousDayOrderIds = normalizePlanningHighlightValues([
      orderId,
      mutationBody.order_id,
      ...(Array.isArray(mutationBody.order_ids) ? mutationBody.order_ids : []),
    ]);
    appendInitialPreloadHighlightKeys(highlightKeys, preloadedPreviousDayLoadingStepIds);
    appendInitialPreloadOrderHighlightKeys(highlightKeys, preloadedPreviousDayOrderIds);
    appendResultInitialPreloadHighlightKeys(highlightKeys, resultPayload, {
      assignmentId: assignmentId || mutationBody.source_truck_id,
      orderIds: preloadedPreviousDayOrderIds,
    });
  }

  const forcedPreloadedPreviousDayOrderIds = normalizePlanningHighlightValues(
    mutationBody.forced_preloaded_previous_day_order_ids,
  );
  if (forcedPreloadedPreviousDayOrderIds.length > 0) {
    appendInitialPreloadOrderHighlightKeys(highlightKeys, forcedPreloadedPreviousDayOrderIds);
    appendResultInitialPreloadHighlightKeys(highlightKeys, resultPayload, {
      assignmentId: assignmentId || mutationBody.target_truck_id,
      deliveryIds: [
        deliveryId,
        ...normalizeDeliveryHighlightIds(deliveryIds),
        ...(Array.isArray(mutationBody.delivery_ids) ? mutationBody.delivery_ids : []),
      ],
      orderIds: forcedPreloadedPreviousDayOrderIds,
    });
  }

  if (DELIVERY_MOVE_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendResultInitialPreloadHighlightKeys(highlightKeys, resultPayload, {
      assignmentId: assignmentId || mutationBody.target_truck_id,
      deliveryIds: [
        deliveryId,
        ...normalizeDeliveryHighlightIds(deliveryIds),
        ...(Array.isArray(mutationBody.delivery_ids) ? mutationBody.delivery_ids : []),
      ],
      orderIds: [
        ...forcedPreloadedPreviousDayOrderIds,
        ...resolveTransitionLoadingOrderIds(deliveryTransition),
      ],
    });
  }

  if (PRELOADED_PREVIOUS_DAY_REMOVAL_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendLoadingOrderHighlightKeys(highlightKeys, [
      orderId,
      mutationBody.order_id,
      ...(Array.isArray(mutationBody.order_ids) ? mutationBody.order_ids : []),
    ]);
    appendLoadingHighlightKeys(highlightKeys, [
      ...(Array.isArray(mutationBody.group_step_ids) ? mutationBody.group_step_ids : []),
      ...(Array.isArray(mutationBody.loading_step_ids) ? mutationBody.loading_step_ids : []),
      ...(Array.isArray(mutationBody.step_ids) ? mutationBody.step_ids : []),
    ]);
  }

  if (safeActionName === "segment_buffer") {
    appendPlanningHighlightKey(highlightKeys, getSegmentBufferHighlightKey(mutationBody.segment_buffer_key));
  }

  if (ROUTE_SEGMENT_MUTATION_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendRouteSegmentHighlightKeys(
      highlightKeys,
      mutationBody.trip_id,
      mutationBody.route_segment_index,
      mutationBody.route_segment_indexes,
    );
  }

  if (TRUCK_OPERATIONAL_STEP_MUTATION_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendTruckOperationalStepHighlightKeys(
      highlightKeys,
      resultPayload,
      assignmentId || mutationBody.truck_id || mutationBody.truckId,
    );
  }

  if (!LOADING_MUTATION_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendTransitionHighlightKeys(highlightKeys, deliveryTransition);
  }
  if (!TARGETED_DELIVERY_LINKED_LOADING_HIGHLIGHT_ACTIONS.has(safeActionName)) {
    appendTransitionHighlightKeys(highlightKeys, linkedStepTransition);
  }

  return highlightKeys;
}

function findOrderTruckContext(resultsData, orderId) {
  if (!orderId || !Array.isArray(resultsData?.assignments)) {
    return null;
  }

  for (const assignment of resultsData.assignments) {
    if (!assignment) {
      continue;
    }

    const hasOrder = (assignment.delivery_items || []).some((item) => item && item.order_id === orderId)
      || (assignment.trips || []).some((trip) => (trip?.orders || []).some((order) => order && order.order_id === orderId));

    if (hasOrder) {
      return {
        orderId,
        truckId: assignment.truck_id || null,
        truckName: assignment.truck_name || assignment.truck_id || null,
      };
    }
  }

  return null;
}

function findDeliveryTruckTransitionContext(resultsData, deliveryId) {
  if (!deliveryId) {
    return null;
  }

  for (const assignment of resultsData?.assignments || []) {
    for (const trip of assignment?.trips || []) {
      for (const client of trip?.clients || []) {
        if (client && client.delivery_id === deliveryId) {
          return {
            deliveryId,
            orderId: client.order_id || null,
            clientName: client.client_name || null,
            truckId: assignment.truck_id || null,
            truckName: assignment.truck_name || assignment.truck_id || null,
          };
        }
      }
    }

    for (const item of assignment?.delivery_items || []) {
      if (item && item.delivery_id === deliveryId) {
        return {
          deliveryId,
          orderId: item.order_id || null,
          clientName: item.client_name || null,
          truckId: assignment.truck_id || null,
          truckName: assignment.truck_name || assignment.truck_id || null,
        };
      }
    }
  }

  for (const delivery of resultsData?.suspended_deliveries || []) {
    if (delivery && delivery.delivery_id === deliveryId) {
      return {
        deliveryId,
        orderId: delivery.order_id || null,
        clientName: delivery.client_name || null,
        truckId: delivery.current_truck_id || null,
        truckName: (resultsData?.assignments || []).find((assignment) => assignment?.truck_id === delivery.current_truck_id)?.truck_name
          || delivery.current_truck_id || null,
      };
    }
  }

  return null;
}

export function buildDeliveryReassignmentToast(previousResultsData, nextResultsData, deliveryId, actionName) {
  const previousContext = findDeliveryTruckTransitionContext(previousResultsData, deliveryId);
  if (!previousContext?.orderId || !previousContext?.truckId) {
    return null;
  }

  const nextContext = findOrderTruckContext(nextResultsData, previousContext.orderId);
  if (!nextContext?.truckId || nextContext.truckId === previousContext.truckId) {
    return null;
  }

  const previousTruckLabel = previousContext.truckName || previousContext.truckId;
  const nextTruckLabel = nextContext.truckName || nextContext.truckId;
  const actionLabel = actionName === "reactivate" ? "réactivation" : "suspension";

  return {
    title: `Camion réattribué après ${actionLabel}`,
    message: `La commande ${previousContext.orderId}${previousContext.clientName ? ` (${previousContext.clientName})` : ""} est maintenant sur ${nextTruckLabel} (avant : ${previousTruckLabel}).`,
    tone: "warning",
    targetTruckId: nextContext.truckId,
  };
}

export function syncResultsAfterPlanningMutation(
  state,
  resultPayload,
  { preferredAssignmentId = null, preferredDeliveryId = null, actionName = "planning_mutation" } = {},
) {
  if (!state) {
    return {
      nextAssignment: null,
      nextTrip: null,
    };
  }

  const currentAssignmentId = state.currentAssignment?.truck_id || null;
  const currentTripId = state.currentTrip?.trip_id || null;

  applyResultsDataToState(state, resultPayload, { actionName });

  const preferredAssignment = preferredAssignmentId
    ? state.assignmentsById.get(preferredAssignmentId) || null
    : null;
  const nextAssignment = preferredAssignment
    || (currentAssignmentId ? state.assignmentsById.get(currentAssignmentId) || null : null)
    || state.firstTripEntry?.assignment
    || state.assignmentList[0]
    || null;
  const selectedTripEntry = currentTripId ? state.tripsById.get(currentTripId) : null;
  let nextTrip = selectedTripEntry && nextAssignment && selectedTripEntry.assignment.truck_id === nextAssignment.truck_id
    ? selectedTripEntry.trip
    : nextAssignment && Array.isArray(nextAssignment.trips) && nextAssignment.trips.length
      ? nextAssignment.trips[0]
      : null;

  if (preferredAssignment && Array.isArray(preferredAssignment.trips) && preferredAssignment.trips.length) {
    const preferredTrip = preferredDeliveryId
      ? preferredAssignment.trips.find((trip) => getTripClientItems(trip).some((client) => client && client.delivery_id === preferredDeliveryId)) || null
      : null;
    nextTrip = preferredTrip || preferredAssignment.trips[0];
  }

  syncClientSelectionState(state);

  return {
    nextAssignment,
    nextTrip: nextAssignment ? nextTrip : null,
  };
}

function findAssignmentByTruckId(resultsData, truckId) {
  const safeTruckId = normalizeInlineText(truckId);
  if (!safeTruckId) {
    return null;
  }

  return resultsData?.assignments?.find((assignment) => normalizeInlineText(assignment?.truck_id) === safeTruckId) || null;
}

// Ce suivi sert aux confirmations (case des heures) : il garde seulement les camions au-delà
// de leurs heures max dont le temps supplémentaire est ouvert côté serveur.
function isAssignmentOvertimeOpenAboveLimit(assignment) {
  return Boolean(
    assignment?.is_overtime_authorized === true
    && getAssignmentOvertimeState(assignment).exceedsMaxWork,
  );
}

export function syncOvertimeWarningTruckIds(state, nextResultsData, { actionName = "", body = null } = {}) {
  if (!state || !nextResultsData || typeof nextResultsData !== "object") {
    return;
  }

  if (actionName === "reset_planning") {
    state.overtimeWarningTruckIds = new Set();
    return;
  }

  const nextTruckIds = new Set(
    Array.from(state.overtimeWarningTruckIds instanceof Set ? state.overtimeWarningTruckIds : []).filter((truckId) => {
      const assignment = findAssignmentByTruckId(nextResultsData, truckId);
      return isAssignmentOvertimeOpenAboveLimit(assignment);
    }),
  );

  const targetTruckId = body && typeof body.target_truck_id === "string"
    ? normalizeInlineText(body.target_truck_id)
    : "";

  if ((actionName === "reactivate" || actionName === "move_delivery") && body?.allow_overtime === true && targetTruckId) {
    const assignment = findAssignmentByTruckId(nextResultsData, targetTruckId);
    if (isAssignmentOvertimeOpenAboveLimit(assignment)) {
      nextTruckIds.add(targetTruckId);
    } else {
      nextTruckIds.delete(targetTruckId);
    }
  }

  state.overtimeWarningTruckIds = nextTruckIds;
}

function getPlanningConfirmationSnapshot(planningSummary) {
  const summary = planningSummary && typeof planningSummary === "object" ? planningSummary : {};
  const confirmedOrderCount = typeof summary.confirmed_count === "number" ? Math.max(0, summary.confirmed_count) : 0;
  const pendingCount = typeof summary.pending_count === "number" ? Math.max(0, summary.pending_count) : 0;
  const confirmedDeliveryCount = typeof summary.delivery_confirmed_count === "number" ? Math.max(0, summary.delivery_confirmed_count) : 0;

  return {
    manualCount: typeof summary.manual_count === "number" ? Math.max(0, summary.manual_count) : 0,
    confirmedOrderCount,
    orderTotalCount: typeof summary.order_total_count === "number" ? Math.max(0, summary.order_total_count) : confirmedOrderCount + pendingCount,
    confirmedDeliveryCount,
    deliveryTotalCount: typeof summary.delivery_total_count === "number" ? Math.max(0, summary.delivery_total_count) : confirmedDeliveryCount,
    suspendedCount: typeof summary.suspended_count === "number" ? Math.max(0, summary.suspended_count) : 0,
  };
}

function formatPlanningConfirmationCount(currentCount, totalCount) {
  return totalCount > 0 ? `${currentCount}/${totalCount}` : `${currentCount}`;
}

const DISPATCH_DAY_CONFIRMATION_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  weekday: "long",
  year: "numeric",
});

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function toSafeInteger(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 0;
  }

  return Math.round(numericValue);
}

function formatDispatchDayConfirmationLabel(dispatchDate, fallbackLabel = "") {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedDispatchDate);

  if (!match) {
    return normalizeInlineText(fallbackLabel) || normalizedDispatchDate || "cette journée";
  }

  const parsedDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalizeInlineText(fallbackLabel) || normalizedDispatchDate || "cette journée";
  }

  return DISPATCH_DAY_CONFIRMATION_LABEL_FORMATTER.format(parsedDate);
}

function buildDispatchDayConfirmationSummaryItems(dispatchDay) {
  const resolvedDispatchDay = dispatchDay && typeof dispatchDay === "object" ? dispatchDay : {};

  return [
    {
      label: "Commandes",
      value: `${toSafeInteger(resolvedDispatchDay.order_count)}`,
      tone: "neutral",
    },
    {
      label: "Prêtes",
      value: `${toSafeInteger(resolvedDispatchDay.ready_count)}`,
      tone: toSafeInteger(resolvedDispatchDay.ready_count) > 0 ? "success" : "neutral",
    },
    {
      label: "Assignées",
      value: `${toSafeInteger(resolvedDispatchDay.assigned_count)}`,
      tone: toSafeInteger(resolvedDispatchDay.assigned_count) > 0 ? "success" : "neutral",
    },
  ];
}

function buildPortalPublicationConfirmationSummaryItems(dispatchDay) {
  const resolvedDispatchDay = dispatchDay && typeof dispatchDay === "object" ? dispatchDay : {};
  const routeCount = toSafeInteger(resolvedDispatchDay.portal_route_count);
  const orderCount = toSafeInteger(resolvedDispatchDay.portal_order_count);
  const driverCount = toSafeInteger(resolvedDispatchDay.portal_driver_count);

  if (routeCount === 1) {
    return [
      {
        label: "Camion",
        multiline: true,
        value: normalizeInlineText(resolvedDispatchDay.portal_truck_name) || "Camion sélectionné",
        tone: "neutral",
      },
      {
        label: "Chauffeur",
        multiline: true,
        value: normalizeInlineText(resolvedDispatchDay.portal_driver_name) || "Chauffeur lié",
        tone: "success",
      },
      {
        label: "Commandes",
        value: `${orderCount}`,
        tone: "neutral",
      },
    ];
  }

  return [
    {
      label: "Tournées",
      value: `${routeCount}`,
      tone: "neutral",
    },
    {
      label: "Commandes",
      value: `${orderCount}`,
      tone: "neutral",
    },
    {
      label: "Chauffeurs",
      value: `${driverCount}`,
      tone: driverCount > 0 ? "success" : "neutral",
    },
  ];
}

function buildPlanningConfirmationSummaryItems(snapshot) {
  return [
    {
      label: "Réaffectées",
      value: `${snapshot.manualCount}`,
      tone: snapshot.manualCount > 0 ? "warning" : "neutral",
    },
    {
      label: "Cmd confirmées",
      value: formatPlanningConfirmationCount(snapshot.confirmedOrderCount, snapshot.orderTotalCount),
      tone: snapshot.confirmedOrderCount > 0 ? "success" : "neutral",
    },
    {
      label: "Suspendues",
      value: `${snapshot.suspendedCount}`,
      tone: snapshot.suspendedCount > 0 ? "warning" : "neutral",
    },
  ];
}

function buildPlanningOptimizeConfirmationSnapshot(planningSummary, { assignments = [], resultsData = {} } = {}) {
  const snapshot = getPlanningConfirmationSnapshot(planningSummary);
  const visibleProtectionSummary = collectVisibleOrderProtectionSummary(assignments, resultsData);
  const hasVisibleProtectionSummary = visibleProtectionSummary.totalVisibleOrders > 0;

  return {
    ...snapshot,
    lockedOrderCount: hasVisibleProtectionSummary
      ? visibleProtectionSummary.protectedOrderIds.length
      : snapshot.confirmedOrderCount,
    orderTotalCount: hasVisibleProtectionSummary
      ? visibleProtectionSummary.totalVisibleOrders
      : snapshot.orderTotalCount,
  };
}

function buildPlanningOptimizeConfirmationSummaryItems(snapshot) {
  return [
    {
      label: "Réaffectées",
      value: `${snapshot.manualCount}`,
      tone: snapshot.manualCount > 0 ? "warning" : "neutral",
    },
    {
      label: "Cmd verrouillées",
      value: formatPlanningConfirmationCount(snapshot.lockedOrderCount, snapshot.orderTotalCount),
      tone: snapshot.lockedOrderCount > 0 ? "success" : "neutral",
    },
    {
      label: "Suspendues",
      value: `${snapshot.suspendedCount}`,
      tone: snapshot.suspendedCount > 0 ? "warning" : "neutral",
    },
  ];
}

export function buildDeliveryMoveConfirmationContent(targetTruckName, { scope = "delivery", protect = true } = {}) {
  const resolvedTruckName = targetTruckName || "ce camion";
  const isGroupScope = scope === "group";
  const unprotectedDescription = isGroupScope
    ? "Les commandes du groupe ne seront pas protégées : le prochain « Optimiser » pourra les déplacer."
    : "La commande ne sera pas protégée : le prochain « Optimiser » pourra la déplacer.";

  return {
    eyebrow: "Réaffectation",
    title: `Confirmer la réaffectation vers ${resolvedTruckName} ?`,
    description: !protect
      ? unprotectedDescription
      : isGroupScope
        ? "Les commandes du groupe seront « Réaffectées » : « Optimiser » les laissera sur ce camion."
        : "La commande sera « Réaffectée » : « Optimiser » la laissera sur ce camion.",
    impactItems: [
      isGroupScope
        ? "Seul le groupe sélectionné est transféré ; les autres commandes ne seront pas déplacées silencieusement."
        : "Seule la commande sélectionnée est transférée ; les autres commandes ne seront pas déplacées silencieusement.",
      "Si l'ajustement local n'est pas faisable sans bouger d'autres commandes, l'action sera refusée.",
    ],
    warningMessage: !protect
      ? ""
      : isGroupScope
        ? "« Libérer » rend les commandes du groupe de nouveau optimisables."
        : "« Libérer » la rend de nouveau optimisable.",
    confirmLabel: "Réaffecter",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

const GENERIC_PLANNING_MUTATION_FAILURE_MESSAGE = "La mise à jour du planning manuel a échoué.";
const PLANNING_MUTATION_VALIDATION_FAILURE_MESSAGE = "La mise à jour du planning n'a pas pu être validée. Rechargez le dispatch puis réessayez.";
const DELIVERY_SUSPEND_GENERIC_FAILURE_MESSAGE = "La suspension n'a pas pu être appliquée. Le planning est resté inchangé.";
const DELIVERY_SUSPEND_STRICT_TIME_WINDOW_FAILURE_MESSAGE = "La commande n'a pas été suspendue. Une autre commande du camion deviendrait non assignable. Le planning est resté inchangé.";
const DELIVERY_SUSPEND_LOCAL_REJECTION_MESSAGES = {
  delivery_client_repeat_forbidden: "La commande n'a pas été suspendue. La tournée restante créerait plusieurs visites séparées chez le même client. Le planning est resté inchangé.",
  ordered_replay_failed: "La commande n'a pas été suspendue. Le mode Rapide n'a pas pu reconstruire la tournée restante en conservant son ordre. Le planning est resté inchangé.",
  split_order_scope: "La commande n'a pas été suspendue. Ses livraisons sont actuellement réparties sur plusieurs camions. Le planning est resté inchangé.",
};
const LEGACY_PRODUCT_COMPATIBILITY_REASON_CODES = new Set([
  "locked_product_incompatible",
  "product_incompatible",
  "truck_product_locked",
]);

function isMissingProductCompatibility(targetCompatibility, detail) {
  const reasonCode = normalizeInlineText(targetCompatibility?.reason_code).toLowerCase();
  const reasonLabel = normalizeInlineText(targetCompatibility?.reason_label).toLowerCase();
  const normalizedDetail = normalizeInlineText(detail).toLowerCase();
  return reasonCode === "product_incompatible"
    && (
      reasonLabel.includes("manquant")
      || reasonLabel.includes("invalide")
      || normalizedDetail.includes("manquant")
      || normalizedDetail.includes("invalide")
    );
}

export function resolvePlanningMutationOperatorErrorMessage(errorMessage) {
  const normalizedErrorMessage = normalizeInlineText(errorMessage);
  if (!normalizedErrorMessage) {
    return normalizedErrorMessage;
  }

  const normalizedLowerMessage = normalizedErrorMessage.toLowerCase();
  const exposesPydanticValidationDiagnostic = (
    /validation errors? for/i.test(normalizedErrorMessage)
    || [
      "errors.pydantic.dev",
      "input should be a valid string",
    ].some((fragment) => normalizedLowerMessage.includes(fragment))
  );

  return exposesPydanticValidationDiagnostic
    ? PLANNING_MUTATION_VALIDATION_FAILURE_MESSAGE
    : normalizedErrorMessage;
}

function resolveTargetCompatibilityRejectedMessage(targetCompatibility = null) {
  if (!targetCompatibility || targetCompatibility.is_current_truck === true || targetCompatibility.is_assignable === true) {
    return "";
  }

  const reasonCode = normalizeInlineText(targetCompatibility?.reason_code).toLowerCase();
  const detail = normalizeInlineText(targetCompatibility?.detail);
  if (
    LEGACY_PRODUCT_COMPATIBILITY_REASON_CODES.has(reasonCode)
    && !isMissingProductCompatibility(targetCompatibility, detail)
  ) {
    return "La liste des camions n'est plus à jour. Rechargez le Dispatch, puis réessayez.";
  }

  if (detail) {
    return detail.toLowerCase().startsWith("cette réaffectation")
      ? detail
      : `Cette réaffectation ne peut pas être appliquée: ${detail}`;
  }

  const reasonLabel = normalizeInlineText(targetCompatibility?.reason_label);
  if (!reasonLabel) {
    return "";
  }

  return reasonLabel.endsWith(".")
    ? `Cette réaffectation ne peut pas être appliquée: ${reasonLabel}`
    : `Cette réaffectation ne peut pas être appliquée: ${reasonLabel}.`;
}

export function resolveDeliverySuspendRejectedMessage(errorMessage) {
  const normalizedErrorMessage = normalizeInlineText(errorMessage);
  if (!normalizedErrorMessage) {
    return DELIVERY_SUSPEND_GENERIC_FAILURE_MESSAGE;
  }

  const normalizedLowerMessage = normalizedErrorMessage.toLowerCase();
  const exposesStrictTimeWindowDiagnostic = [
    "sort de la borne effective",
    "fenêtre canonique",
    "hors de sa fenêtre",
    "window_hard_violation",
  ].some((fragment) => normalizedLowerMessage.includes(fragment));
  if (exposesStrictTimeWindowDiagnostic) {
    return DELIVERY_SUSPEND_STRICT_TIME_WINDOW_FAILURE_MESSAGE;
  }

  for (const [reasonCode, message] of Object.entries(DELIVERY_SUSPEND_LOCAL_REJECTION_MESSAGES)) {
    if (normalizedLowerMessage.includes(reasonCode)) {
      return message;
    }
  }

  const exposesLocalizedTechnicalDiagnostic = [
    "localized_",
    "local_patch_",
    "la suspension locale n'a pas pu",
    "aucune tournée n'a été recalculée globalement",
  ].some((fragment) => normalizedLowerMessage.includes(fragment));
  if (exposesLocalizedTechnicalDiagnostic) {
    return "La commande n'a pas été suspendue parce que la tournée restante n'a pas pu être reconstruite de façon cohérente. "
      + `Détail serveur: ${normalizedErrorMessage}`;
  }

  return normalizedErrorMessage;
}

export function resolveDeliveryMoveRejectedMessage(
  errorMessage,
  { targetCompatibility = null } = {},
) {
  const normalizedErrorMessage = resolvePlanningMutationOperatorErrorMessage(errorMessage);
  const compatibilityRejectedMessage = resolveTargetCompatibilityRejectedMessage(targetCompatibility);

  if (
    compatibilityRejectedMessage
    && (!normalizedErrorMessage || normalizedErrorMessage === GENERIC_PLANNING_MUTATION_FAILURE_MESSAGE)
  ) {
    return compatibilityRejectedMessage;
  }

  // Refus du serveur tel quel : il repère lui-même le dépassement d'heures max.
  return normalizedErrorMessage;
}

export function buildDeliverySuspendConfirmationContent() {
  return {
    eyebrow: "Suspension",
    title: "Confirmer la suspension de cette commande ?",
    description: "La commande sera retirée de la flotte active jusqu'à sa réactivation explicite.",
    impactItems: [
      "Tous les chargements et déchargements associés seront retirés des tournées actives juste après validation.",
      "La commande restera suspendue tant qu'une action explicite de remise en optimisation n'est pas demandée.",
    ],
    confirmLabel: "Suspendre",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildDeliveryFreezeConfirmationContent() {
  return {
    eyebrow: "Gel",
    title: "Confirmer le gel de cette commande ?",
    description: "La commande et celles chargées au même endroit sur ce camion seront gelées.",
    impactItems: [
      "Leurs livraisons passeront à « Gelée ».",
      "Leurs autres actions seront masquées jusqu'au dégel.",
      "« Optimiser » ne les déplacera pas tant qu'elles restent gelées.",
    ],
    confirmLabel: "Geler",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildDeliveryUnfreezeConfirmationContent() {
  return {
    eyebrow: "Gel",
    title: "Confirmer le dégel de cette commande ?",
    description: "La commande sera dégelée, sans relancer « Optimiser ».",
    impactItems: [
      "Les commandes chargées au même endroit sur ce camion seront aussi dégelées.",
      "Leurs autres actions seront de nouveau disponibles.",
      "La commande reste sur ce camion jusqu'au prochain changement ou « Optimiser ».",
    ],
    confirmLabel: "Dégeler",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildErrorSuspendConfirmationContent(entry = {}) {
  const orderLabel = normalizeInlineText(entry.orderLabel)
    || (normalizeInlineText(entry.suspendTargetOrderId || entry.orderId) ? `Commande ${normalizeInlineText(entry.suspendTargetOrderId || entry.orderId)}` : "cette commande");
  const reasonLabel = normalizeInlineText(entry.initialReasonLabel || entry.cause);
  return {
    eyebrow: "Centre des alertes",
    title: "Transférer cette commande dans Suspendues ?",
    description: `${orderLabel} sera retirée de la flotte active depuis l'erreur métier sélectionnée.`,
    impactItems: [
      "Toutes les livraisons de cette commande seront suspendues ensemble.",
      "La commande restera consultable dans Suspendues et pourra être réintégrée explicitement.",
      ...(reasonLabel ? [`Motif initial conservé: ${reasonLabel}.`] : []),
    ],
    confirmLabel: "Transférer",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildErrorSuspendGroupConfirmationContent(entries = []) {
  const safeEntries = Array.isArray(entries) ? entries.filter(Boolean) : [];
  const count = safeEntries.length;
  const firstEntry = safeEntries[0] || {};
  const firstDeliveryItem = Array.isArray(firstEntry.deliveryItems) ? firstEntry.deliveryItems[0] : null;
  const clientLabel = normalizeInlineText(firstDeliveryItem?.label || firstEntry.clientName);
  const totalQuantity = safeEntries.reduce((total, entry) => (
    Number.isFinite(entry?.quantityTons) ? total + entry.quantityTons : total
  ), 0);
  const quantityLabel = totalQuantity > 0
    ? `${Number.isInteger(totalQuantity) ? totalQuantity : totalQuantity.toFixed(1)} t`
    : "";

  return {
    eyebrow: "Centre des alertes",
    title: `Transférer ${count} commande${count > 1 ? "s" : ""} dans Suspendues ?`,
    description: clientLabel
      ? `${clientLabel} sera retiré de la liste Non assignables et conservé dans Suspendues.`
      : "Ce groupe sera retiré de la liste Non assignables et conservé dans Suspendues.",
    impactItems: [
      `Les ${count} commande${count > 1 ? "s" : ""} du groupe seront transférées une par une avec le motif initial.`,
      "Chaque commande restera consultable dans Suspendues et pourra être réintégrée explicitement.",
      ...(quantityLabel ? [`Volume total du groupe: ${quantityLabel}.`] : []),
    ],
    confirmLabel: "Transférer le groupe",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildDeliveryUnconfirmConfirmationContent(planningSummary, { scope = "single", count = 0 } = {}) {
  const isGroup = scope === "group" && count >= 2;

  if (isGroup) {
    return {
      eyebrow: "Protection groupée",
      title: `Retirer la protection de ${count} commandes ?`,
      description: "Ces commandes redeviendront déplaçables par Réaffecter et Optimiser tant qu'aucune nouvelle protection n'est posée.",
      impactItems: [
        `La protection explicite sera retirée immédiatement pour les ${count} commandes du groupe.`,
        "Les commandes resteront visibles sur leur camion actuel jusqu'à un prochain clic sur Optimiser.",
        "Lors de cette prochaine optimisation, elles pourront être déplacées si vous ne les protégez pas ensuite.",
      ],
      warningMessage: "Après retrait, protégez de nouveau chaque commande pour la figer avant ou après une prochaine optimisation.",
      confirmLabel: "Retirer la protection du groupe",
      cancelLabel: "Annuler",
      tone: "warning",
    };
  }

  return {
    eyebrow: "Protection commande",
    title: "Retirer la protection de cette commande ?",
    description: "La commande redeviendra déplaçable par Réaffecter et Optimiser tant qu'aucune nouvelle protection n'est posée.",
    impactItems: [
      "La protection explicite sera retirée immédiatement.",
      "La commande restera visible sur son camion actuel jusqu'à un prochain clic sur Optimiser.",
    ],
    warningMessage: "Après retrait, protégez de nouveau la commande pour la figer avant ou après une prochaine optimisation.",
    confirmLabel: "Retirer la protection",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

// Libérer et réactiver retirent les temps ajoutés de la commande (clear_order_manual_state).
const MANUAL_BUFFER_RESET_IMPACT = "Les temps ajoutés avec « Buf » au chargement et aux livraisons seront retirés.";

export function buildDeliveryReleaseConfirmationContent(planningSummary, { scope = "single", count = 0 } = {}) {
  const isGroup = scope === "group" && count >= 2;

  if (isGroup) {
    return {
      eyebrow: "Libération groupée",
      title: `Confirmer la libération de ${count} commandes ?`,
      description: "Ces commandes redeviendront optimisables et resteront sur leur camion actuel jusqu'à la prochaine optimisation.",
      impactItems: [
        `Les verrous manuels de réaffectation seront retirés immédiatement pour les ${count} commandes du groupe.`,
        "Les commandes resteront visibles sur leur camion actuel jusqu'à un prochain clic sur Optimiser.",
        "Lors de cette prochaine optimisation, elles pourront être déplacées vers un autre camion si vous ne les protégez pas ensuite.",
        MANUAL_BUFFER_RESET_IMPACT,
      ],
      warningMessage: "Après libération, protégez de nouveau chaque commande pour la figer avant ou après une prochaine optimisation.",
      confirmLabel: "Libérer le groupe",
      cancelLabel: "Annuler",
      tone: "warning",
    };
  }

  return {
    eyebrow: "Libération",
    title: "Confirmer la libération de cette commande ?",
    description: "La commande redeviendra optimisable et restera sur son camion actuel jusqu'à la prochaine optimisation.",
    impactItems: [
      "Le verrou manuel de réaffectation sera retiré immédiatement.",
      "La commande restera visible sur son camion actuel jusqu'à un prochain clic sur Optimiser.",
      "Lors de cette prochaine optimisation, elle pourra être déplacée vers un autre camion si vous ne la protégez pas ensuite.",
      MANUAL_BUFFER_RESET_IMPACT,
    ],
    warningMessage: "Après libération, protégez de nouveau la commande pour la figer avant ou après une prochaine optimisation.",
    confirmLabel: "Libérer",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildDeliveryReactivateConfirmationContent(targetTruckName = "", { protect = true } = {}) {
  const resolvedTargetTruckName = typeof targetTruckName === "string" ? targetTruckName.trim() : "";
  const isTargetedReactivate = Boolean(resolvedTargetTruckName);

  if (isTargetedReactivate) {
    const moveContent = buildDeliveryMoveConfirmationContent(resolvedTargetTruckName, { protect });
    return { ...moveContent, impactItems: [...moveContent.impactItems, MANUAL_BUFFER_RESET_IMPACT] };
  }

  // Sans place sur un camion, le serveur refuse : aucun recalcul de la journée.
  return {
    eyebrow: "Réactivation",
    title: "Réactiver cette commande ?",
    description: "Elle ira sur le camion qui la prend le mieux.",
    impactItems: [
      "S'il manque de la place : refus, elle reste suspendue.",
      "Ses temps « Buf » sont retirés.",
    ],
    confirmLabel: "Réactiver",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildOrderProtectionConfirmationContent(stepType = "delivery") {
  const resolvedStepType = normalizeInlineText(stepType).toLowerCase();

  if (resolvedStepType === "loading") {
    return {
      eyebrow: "Protection chargement",
      title: "Protéger ce chargement ?",
      description: "Le chargement sera protégé dans l'état courant du planning.",
      impactItems: [
        "Cette étape de chargement passera immédiatement en statut protégé.",
        "Ses livraisons sont protégées en même temps.",
      ],
      confirmLabel: "Protéger",
      cancelLabel: "Annuler",
      tone: "accent",
    };
  }

  return {
    eyebrow: "Protection commande",
    title: "Protéger cette commande ?",
    description: "Le déchargement sera protégé et le chargement lié sera confirmé si nécessaire pour verrouiller la commande sur ce camion.",
    impactItems: [
      "La commande restera figée sur ce camion tant que vous ne retirez pas cette protection.",
      "Réaffecter et Optimiser ne rebougeront pas cette commande tant que la protection restera active.",
    ],
    confirmLabel: "Protéger",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

export function buildTruckProtectionConfirmationContent(truckName, isProtected = false) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";

  if (isProtected) {
    return {
      eyebrow: "Protection camion",
      title: `Retirer la protection de ${resolvedTruckName} ?`,
      description: "Le camion redeviendra disponible pour Réaffecter et Optimiser, sans retirer les protections commandes posées séparément.",
      impactItems: [
        "Les commandes déjà protégées séparément sur ce camion resteront confirmées tant qu'aucune déprotection explicite n'est demandée sur elles.",
        "Le camion pourra de nouveau être choisi comme cible ou source par les mutations locales et la prochaine optimisation.",
      ],
      confirmLabel: "Déprotéger le camion",
      cancelLabel: "Annuler",
      tone: "warning",
    };
  }

  return {
    eyebrow: "Protection camion",
    title: `Protéger ${resolvedTruckName} ?`,
    description: "Le camion sera figé pour Réaffecter et Optimiser jusqu'à retrait explicite de cette protection, sans protéger visuellement ses commandes.",
    impactItems: [
      "Les commandes actuellement présentes sur ce camion resteront verrouillées dessus sans recevoir automatiquement le symbole de protection commande.",
      "Aucune nouvelle commande non protégée ne sera envoyée sur ce camion tant que la protection restera active.",
    ],
    confirmLabel: "Protéger le camion",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

export function buildTruckDispatchDisabledConfirmationContent(
  truckName,
  isDispatchDisabled = false,
  { hasOrders = false, isTruckProtected = false } = {},
) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";
  // La désactivation retire la protection du camion : on ne l'annonce que s'il est protégé.
  const protectionItems = isTruckProtected ? ["Le camion perd sa protection."] : [];

  if (isDispatchDisabled) {
    return {
      eyebrow: "Disponibilité dispatch",
      title: `Réactiver ${resolvedTruckName} dans ce dispatch ?`,
      description: "Le camion redeviendra disponible pour les réaffectations, les previews locales et la prochaine optimisation du dispatch courant.",
      impactItems: [
        "Le camion restera visible dans la flotte et pourra de nouveau servir de cible ou de support après cette réactivation.",
        "Aucune commande n'est déplacée immédiatement lors de cette réactivation.",
      ],
      confirmLabel: "Réactiver dans le dispatch",
      cancelLabel: "Annuler",
      tone: "accent",
    };
  }

  return {
    eyebrow: "Disponibilité dispatch",
    title: `Désactiver ${resolvedTruckName} pour ce dispatch ?`,
    description: hasOrders
      ? "Ses commandes iront dans Suspendues."
      : "Le camion restera visible, mais sera exclu du dispatch courant sans toucher à la fiche maître camion.",
    impactItems: hasOrders
      ? protectionItems
      : [
        "Le camion restera visible mais bloqué pour Réaffecter, les previews locales et Optimiser tant que cet état dispatch restera actif.",
        "Vous pourrez le réactiver plus tard sans déplacer immédiatement de commande.",
        ...protectionItems,
      ],
    confirmLabel: "Désactiver pour ce dispatch",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildTruckOrderProtectionConfirmationContent(truckName, areAllOrdersProtected = false) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";

  if (areAllOrdersProtected) {
    return {
      eyebrow: "Protection commandes",
      title: `Retirer la protection des commandes de ${resolvedTruckName} ?`,
      description: "Optimiser et les réaffectations locales pourront de nouveau les déplacer.",
      confirmLabel: "Déprotéger les commandes",
      cancelLabel: "Annuler",
      tone: "warning",
    };
  }

  return {
    eyebrow: "Protection commandes",
    title: `Protéger toutes les commandes de ${resolvedTruckName} ?`,
    description: "Optimiser et les réaffectations locales ne les déplaceront plus.",
    confirmLabel: "Protéger les commandes",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

export function buildTruckOvertimeConfirmationContent(
  truckName,
  isAuthorized = false,
  { isDisableLocked = false } = {},
) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";

  if (isAuthorized) {
    const impactItems = [
      "Des commandes libres (ni protégées, ni confirmées, ni gelées, ni réaffectées) pourront être retirées de ce camion.",
      "Les commandes impossibles à replacer redeviendront non assignées dans le planning courant.",
    ];

    if (isDisableLocked) {
      impactItems.unshift(
        "Action indisponible tant que le camion reste protégé et que toutes ses commandes sont déjà protégées.",
      );
    }

    return {
      eyebrow: "Temps supplémentaire",
      title: `Refermer le temps supplémentaire de ${resolvedTruckName} ?`,
      description: "Le camion sera ramené sous ses heures max. Des commandes pourront être retirées du camion et redistribuées par le recalcul, sauf les protégées, confirmées, gelées ou réaffectées.",
      impactItems,
      confirmLabel: "Refermer le temps supplémentaire",
      cancelLabel: "Annuler",
      tone: "warning",
    };
  }

  return {
    eyebrow: "Temps supplémentaire",
    title: `Ouvrir le temps supplémentaire pour ${resolvedTruckName} ?`,
    description: "Le camion pourra dépasser ses heures max lors d'un glisser-déposer ou d'une réaffectation. Rien ne bouge tout de suite.",
    impactItems: [
      "La durée du camion devient orange s'il dépasse ses heures max.",
      "Les prochains déplacements vers ce camion ne redemandent pas l'autorisation. Le temps supplémentaire se referme seul quand un changement ramène le camion sous sa limite.",
    ],
    confirmLabel: "Ouvrir le temps supplémentaire",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

export function buildTruckDompeurMixedLoadConfirmationContent(
  truckName,
  isAuthorized = false,
  { isDompeurTruck = false } = {},
) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";

  if (!isDompeurTruck) {
    return {
      eyebrow: "Regroupement Dompeur",
      title: "Option disponible seulement sur un camion Dompeur",
      description: "Le regroupement de plusieurs commandes dans un même chargement reste désactivé pour ce camion.",
      impactItems: [
        "Comportement standard: chargement commande A -> livraison, puis chargement commande B -> livraison.",
        "Le bouton Optimiser ce camion n'est pas modifié.",
      ],
      confirmLabel: "Fermer",
      cancelLabel: "Annuler",
      tone: "neutral",
    };
  }

  if (isAuthorized) {
    return {
      eyebrow: "Regroupement Dompeur",
      title: `Désactiver le regroupement Dompeur pour ${resolvedTruckName} ?`,
      description: "Actuellement, les réaffectations manuelles vers ce Dompeur peuvent regrouper des commandes compatibles dans une séquence à plusieurs chargements suivie d'une seule livraison. En désactivant l'option, le camion courant est recalculé et les prochaines réaffectations reviennent au comportement standard.",
      impactItems: [
        "Les regroupements compatibles déjà présents sur ce camion seront remis en livraisons séparées.",
        "Après: chargement commande A -> livraison, puis chargement commande B -> livraison.",
      ],
      confirmLabel: "Désactiver le regroupement",
      cancelLabel: "Annuler",
      tone: "warning",
    };
  }

  return {
    eyebrow: "Regroupement Dompeur",
    title: `Autoriser le regroupement Dompeur pour ${resolvedTruckName} ?`,
    description: "Sans cette option, un Dompeur garde le comportement standard d'une livraison après chaque chargement. En l'activant, le camion courant est recalculé et les commandes déjà compatibles sur ce camion peuvent être regroupées. Le bouton Optimiser ce camion ignore toujours cette option.",
    impactItems: [
      "Avant: chargement commande A -> livraison, puis chargement commande B -> livraison.",
      "Après, si compatible: chargement commande A -> chargement commande B -> une livraison commune.",
    ],
    confirmLabel: "Autoriser le regroupement",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

export function normalizeTruckPreloadTimeValue(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const hours = `${value.getHours()}`.padStart(2, "0");
    const minutes = `${value.getMinutes()}`.padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  const normalizedValue = normalizeInlineText(value);
  if (!normalizedValue) {
    return "";
  }

  const directTimeMatch = /(?:^|T)(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/.exec(normalizedValue);
  if (directTimeMatch) {
    return `${directTimeMatch[1].padStart(2, "0")}:${directTimeMatch[2]}`;
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const hours = `${parsedDate.getHours()}`.padStart(2, "0");
  const minutes = `${parsedDate.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function formatTruckPreloadTimeLabel(value) {
  const normalizedValue = normalizeTruckPreloadTimeValue(value);
  if (!normalizedValue) {
    return "";
  }

  const [hours, minutes] = normalizedValue.split(":");
  return `${hours} h ${minutes}`;
}

// excludeEndOfDayLoads : un Chargé PM est planifié dans le dispatch de la veille, il n'a pas d'heure de préchargement à saisir.
export function resolveAssignmentHasPreloadedPreviousDay(assignment = null, { excludeEndOfDayLoads = false } = {}) {
  const endOfDayLoadOrderIds = new Set(excludeEndOfDayLoads && Array.isArray(assignment?.end_of_day_load_source_preloads)
    ? assignment.end_of_day_load_source_preloads.flatMap((item) => (Array.isArray(item?.order_ids) ? item.order_ids : []).map(normalizeInlineText))
    : []);
  const isEndOfDayLoadItem = (item) => endOfDayLoadOrderIds.has(normalizeInlineText(item?.order_id));
  const operationalSteps = Array.isArray(assignment?.operational_steps)
    ? assignment.operational_steps.filter(Boolean)
    : [];
  if (operationalSteps.some((step) => Boolean(step?.is_preloaded_previous_day) && !isEndOfDayLoadItem(step))) {
    return true;
  }

  const orders = Array.isArray(assignment?.orders) ? assignment.orders.filter(Boolean) : [];
  return orders.some((order) => !isEndOfDayLoadItem(order) && (
    Boolean(order?.is_preloaded_previous_day)
    || (Array.isArray(order?.deliveries) && order.deliveries.some((delivery) => Boolean(delivery?.is_preloaded_previous_day)))
  ));
}

export function countTruckPreloadTimeOverrides(assignments = []) {
  const seenTruckIds = new Set();

  return (Array.isArray(assignments) ? assignments : []).reduce((count, assignment, index) => {
    if (!normalizeTruckPreloadTimeValue(assignment?.truck_preload_time)) {
      return count;
    }

    const truckKey = normalizeInlineText(assignment?.truck_id) || `assignment-${index}`;
    if (seenTruckIds.has(truckKey)) {
      return count;
    }

    seenTruckIds.add(truckKey);
    return count + 1;
  }, 0);
}

export function buildClearTruckPreloadTimeConfirmationContent(truckName, preloadTime = "") {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";
  const preloadTimeLabel = formatTruckPreloadTimeLabel(preloadTime);
  const hourSubject = preloadTimeLabel ? `l’heure ${preloadTimeLabel}` : "l’heure manuelle";

  return {
    eyebrow: "Préchargement",
    title: `Effacer ${hourSubject} de ${resolvedTruckName} ?`,
    description: "Seule l’heure manuelle disparaîtra des rapports du dispatch courant; le preload et les tournées resteront inchangés.",
    confirmLabel: "Effacer",
    cancelLabel: "Annuler",
    contentDensity: "compact",
    panelSize: "compact",
    tone: "danger",
  };
}

export function buildTruckPreloadTimeConfirmationContent(
  truckName,
  { isEligible = false, isEndOfDayLoadOnly = false, preloadTime = "" } = {},
) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";
  const normalizedPreloadTime = normalizeTruckPreloadTimeValue(preloadTime);
  const preloadTimeLabel = formatTruckPreloadTimeLabel(normalizedPreloadTime);

  if (!isEligible) {
    const impactItems = isEndOfDayLoadOnly ? [] : [
      "Ajoutez au moins une commande marquée Chargée la veille sur ce camion pour rendre cette saisie disponible.",
    ];

    if (preloadTimeLabel) {
      impactItems.push(`L'heure manuelle actuellement enregistrée (${preloadTimeLabel}) peut toujours être effacée avec la corbeille associée.`);
    }

    return {
      eyebrow: "Préchargement",
      title: isEndOfDayLoadOnly ? `${resolvedTruckName} porte un Chargé PM` : `${resolvedTruckName} n'est pas en preload la veille`,
      description: isEndOfDayLoadOnly
        ? "Un Chargé PM est planifié dans le dispatch de la veille : il n'a pas d'heure de préchargement à saisir."
        : "Aucune commande actuellement affectée à ce camion n'est marquée Chargée la veille.",
      impactItems,
      confirmLabel: "Fermer",
      cancelLabel: "Fermer",
      tone: "neutral",
    };
  }

  if (preloadTimeLabel) {
    return {
      eyebrow: "Préchargement",
      title: `Modifier l'heure de préchargement de ${resolvedTruckName} ?`,
      description: `L'heure manuelle actuelle (${preloadTimeLabel}) sera reprise par le dispatch courant, le rapport Meunerie et le PDF de préchargement. Laissez le champ vide pour la retirer.`,
      impactItems: [
        "L'heure vaut pour ce dispatch seulement : la fiche du camion ne change pas.",
        "Seuls les camions portant au moins une commande chargée la veille peuvent enregistrer cette heure manuelle.",
      ],
      confirmLabel: "Mettre à jour l'heure",
      cancelLabel: "Annuler",
      tone: "accent",
    };
  }

  return {
    eyebrow: "Préchargement",
    title: `Saisir une heure de préchargement pour ${resolvedTruckName} ?`,
    description: "Cette heure manuelle sera reprise par le dispatch courant, le rapport Meunerie et le PDF de préchargement.",
    impactItems: [
      "L'heure vaut pour ce dispatch seulement : la fiche du camion ne change pas.",
      "L'heure manuelle ne s'applique qu'aux camions portant au moins une commande chargée la veille dans l'affectation courante.",
    ],
    confirmLabel: "Enregistrer l'heure",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

function countTruckRouteOptimizationTrips(assignment = null) {
  const trips = Array.isArray(assignment?.trips) ? assignment.trips : [];
  return trips.reduce((count, trip) => {
    const stops = Array.isArray(trip?.routable_stops)
      ? trip.routable_stops
      : Array.isArray(trip?.stops)
        ? trip.stops
        : [];
    const routableStopCount = stops.filter((stop) => stop && stop.is_routable !== false).length;
    return count + (routableStopCount >= 2 ? 1 : 0);
  }, 0);
}

function countTruckManuallyReorderedCommands(assignment = null) {
  const seenKeys = new Set();

  const visitItem = (item, fallbackKey) => {
    if (
      !item
      || typeof item !== "object"
      || (!item.is_manually_reordered && !normalizeInlineText(item.manual_reorder_scope))
    ) {
      return;
    }
    const key = normalizeInlineText(item.order_id)
      || normalizeInlineText(item.delivery_id)
      || normalizeInlineText(item.step_id)
      || fallbackKey;
    if (key) {
      seenKeys.add(key);
    }
  };

  (Array.isArray(assignment?.orders) ? assignment.orders : []).forEach((order, index) => {
    visitItem(order, `order-${index}`);
  });
  (Array.isArray(assignment?.delivery_items) ? assignment.delivery_items : []).forEach((delivery, index) => {
    visitItem(delivery, `delivery-${index}`);
  });
  (Array.isArray(assignment?.operational_steps) ? assignment.operational_steps : []).forEach((step, index) => {
    visitItem(step, `step-${index}`);
  });

  return seenKeys.size;
}

export function buildTruckRouteOptimizationConfirmationContent(truckName, { assignment = null, resultsData = null } = {}) {
  const resolvedTruckName = normalizeInlineText(truckName) || "ce camion";
  const routeCount = countTruckRouteOptimizationTrips(assignment);
  const manuallyReorderedCount = countTruckManuallyReorderedCommands(assignment);
  const routeSubject = routeCount > 1
    ? `ses ${routeCount} tournées`
    : routeCount === 1
      ? "sa tournée"
      : "ses livraisons";

  return {
    eyebrow: "Optimisation camion",
    title: `Optimiser ${resolvedTruckName} ?`,
    description: `Réoptimise ${routeSubject} avec les données Data Manager, puis recalcule horaires, distances et routes HERE.\nLes commandes restent sur ce camion.`,
    contentDensity: "compact",
    panelSize: "comfortable",
    impactItems: [],
    summaryItems: [],
    confirmLabel: "Optimiser ce camion",
    cancelLabel: "Annuler",
    tone: "accent",
    ...(manuallyReorderedCount > 0 ? { warningVariant: REORDERED_PROTECTION_TONE } : {}),
    warningMessage: manuallyReorderedCount > 0
      ? "Réordonnancement protégé : le camion sera recalculé (horaires, distances et trajets) en conservant son ordre actuel. "
        // Camion « Non mis à jour » : le recalcul qui lève l'avis est gardé même sans gain (décision R2).
        + (buildExcludedTruckRefreshByTruckId(resultsData || {}).get(normalizeInlineText(assignment?.truck_id))?.refreshStatus === "failed"
          ? "Il est « Non mis à jour » : le recalcul qui lève l'avis sera gardé, même sans gain."
          : "Sans amélioration, le dispatch restera inchangé.")
      : "",
  };
}

export function buildTruckBulkReassignmentTargetOptions(currentTruckId, truckOptions = []) {
  const safeCurrentTruckId = normalizeInlineText(currentTruckId);
  const seenTruckIds = new Set();

  return (Array.isArray(truckOptions) ? truckOptions : []).reduce((items, option) => {
    const truckId = normalizeInlineText(option?.truckId || option?.assignmentId || option?.value);
    const isDispatchDisabled = Boolean(
      option?.isDispatchDisabled
      || option?.is_dispatch_disabled
      || option?.assignment?.is_dispatch_disabled,
    );
    if (!truckId || truckId === safeCurrentTruckId || seenTruckIds.has(truckId) || isDispatchDisabled) {
      return items;
    }

    seenTruckIds.add(truckId);
    const truckName = normalizeInlineText(option?.truckName || option?.label) || truckId;
    // Remorque sans camion ce jour-là : listée mais grisée, comme dans les cibles de réaffectation.
    const isTractorMissing = option?.isTractorMissing === true;
    items.push({
      truckId,
      truckName,
      label: isTractorMissing ? `${truckName} — Camion à choisir` : truckName,
      meta: normalizeInlineText(option?.meta),
      ...(isTractorMissing ? { isTractorMissing } : {}),
    });
    return items;
  }, []);
}

export function resolveTruckBulkReassignmentTargetOption(currentTruckId, truckOptions = [], selectedTargetTruckId = "") {
  const options = buildTruckBulkReassignmentTargetOptions(currentTruckId, truckOptions);
  const safeSelectedTargetTruckId = normalizeInlineText(selectedTargetTruckId);

  if (!options.length) {
    return null;
  }

  const selectableOptions = options.filter((option) => !option.isTractorMissing);
  return selectableOptions.find((option) => option.truckId === safeSelectedTargetTruckId) || selectableOptions[0] || null;
}

export function buildTruckBulkReassignmentConfirmationContent(sourceTruckName, targetTruckName) {
  const resolvedSourceTruckName = normalizeInlineText(sourceTruckName) || "ce camion";
  const resolvedTargetTruckName = normalizeInlineText(targetTruckName) || "le camion cible";

  return {
    eyebrow: "Réaffectation groupée",
    title: `Réaffecter toutes les commandes de ${resolvedSourceTruckName} vers ${resolvedTargetTruckName} ?`,
    description: "Toutes les commandes actuellement présentes sur le camion source seront transférées explicitement vers le camion cible. L'opération est tout ou rien.",
    impactItems: [
      "Les commandes protégées, confirmées ou posées manuellement sont incluses dans ce transfert explicite.",
      "Si une seule commande ne peut pas vraiment être placée sur le camion cible, rien ne sera persisté.",
    ],
    confirmLabel: "Réaffecter toutes les commandes",
    cancelLabel: "Annuler",
    tone: "warning",
  };
}

function buildPlanningImpactItems(snapshot, messages) {
  const impactItems = [];

  if (snapshot.manualCount > 0) {
    impactItems.push(messages.manual(snapshot.manualCount));
  }
  if (snapshot.confirmedOrderCount > 0) {
    impactItems.push(messages.confirmedOrders(snapshot.confirmedOrderCount));
  }
  if (snapshot.confirmedDeliveryCount > 0) {
    impactItems.push(messages.confirmedDeliveries(snapshot.confirmedDeliveryCount));
  }
  if (snapshot.suspendedCount > 0) {
    impactItems.push(messages.suspended(snapshot.suspendedCount));
  }
  if (!impactItems.length) {
    impactItems.push(messages.empty);
  }

  impactItems.push(messages.final);
  return impactItems;
}

export function buildPlanningResetConfirmationContent(planningSummary) {
  const snapshot = getPlanningConfirmationSnapshot(planningSummary);
  const impactItems = buildPlanningImpactItems(snapshot, {
    manual: (count) => `${count} réaffectation(s) manuelle(s) seront annulées.`,
    confirmedOrders: (count) => `${count} commande(s) confirmée(s) dans le planning seront remises à zéro si elles n'ont aucune exécution réelle.`,
    confirmedDeliveries: (count) => `${count} ferme(s) confirmée(s) dans le planning seront remises à zéro si elles n'ont aucune exécution réelle.`,
    suspended: (count) => `${count} commande(s) suspendue(s) seront remises en optimisation comme non assignées.`,
    empty: "Les réaffectations et confirmations de planification sans exécution réelle seront réinitialisées.",
    final: "Les tournées, cartes et affectations visibles seront rechargées immédiatement.",
  });
  impactItems.splice(-1, 0, "Les bris de camion, les choix de traverse Sorel-Tracy et les divisions en deux chargements seront aussi effacés.");
  const sharedTruckCount = Number(planningSummary?.compartment_sharing_truck_count) || 0;
  if (sharedTruckCount > 0) {
    impactItems.splice(-1, 0, `Les compartiments partagés de ${sharedTruckCount} camion${sharedTruckCount > 1 ? "s" : ""} seront défaits.`);
  }

  return {
    eyebrow: "Planning manuel",
    title: "Confirmer la réinitialisation ?",
    description: "La réinitialisation est refusée dès qu'un chargement ou une livraison réels existent. Sans exécution, elle remet à zéro les ajustements du planning, sauf les commandes Chargé PM provenant du dispatch précédent.",
    impactItems,
    summaryItems: buildPlanningConfirmationSummaryItems(snapshot),
    confirmLabel: "Réinitialiser",
    cancelLabel: "Annuler",
    tone: "warning",
    warningMessage: "Les commandes Chargé PM provenant du dispatch précédent restent chargées sur leur camion. Les données sources du Data Manager restent la référence après reconstruction.",
  };
}

export function buildPlanningOptimizeConfirmationContent(planningSummary, options = {}) {
  const snapshot = buildPlanningOptimizeConfirmationSnapshot(planningSummary, options);
  const includeSuspendedDeliveries = options?.includeSuspendedDeliveries === true;
  const impactItems = [];
  const protectionItems = [];

  if (snapshot.manualCount > 0) {
    const plural = snapshot.manualCount === 1 ? "" : "s";
    protectionItems.push(`${snapshot.manualCount} réaffectation${plural} manuelle${plural} conservée${plural} comme verrou${plural}.`);
  }
  if (snapshot.confirmedOrderCount > 0) {
    const plural = snapshot.confirmedOrderCount === 1 ? "" : "s";
    protectionItems.push(`${snapshot.confirmedOrderCount} commande${plural} protégée${plural} reste${plural ? "nt" : ""} verrouillée${plural}.`);
  }
  if (protectionItems.length) {
    impactItems.push(protectionItems.join(" "));
  }
  if (snapshot.suspendedCount > 0) {
    const plural = snapshot.suspendedCount === 1 ? "" : "s";
    impactItems.push(
      `${snapshot.suspendedCount} commande${plural} suspendue${plural} ${includeSuspendedDeliveries
        ? `réintégrée${plural} dans le calcul` : `exclue${plural} du calcul`}.`,
    );
  }
  if (!impactItems.length) {
    impactItems.push("Calcul à partir des données actuelles, sans ajustement manuel.");
  }

  return {
    eyebrow: "Re-optimisation",
    title: "Confirmer la re-optimisation de la flotte ?",
    description: "Recalcule les tournées, la carte et les indicateurs en respectant les verrous.",
    impactItems,
    summaryItems: buildPlanningOptimizeConfirmationSummaryItems(snapshot),
    confirmLabel: "Optimiser maintenant",
    cancelLabel: "Annuler",
    tone: "accent",
  };
}

export function buildDispatchDayTransitionConfirmationContent(dispatchDay, action = "close") {
  const safeAction = action === "reopen"
    ? "reopen"
    : action === "invalidate"
      ? "invalidate"
      : action === "publish_portal"
        ? "publish_portal"
        : action === "unpublish_portal"
          ? "unpublish_portal"
          : "close";
  const resolvedDispatchDay = dispatchDay && typeof dispatchDay === "object" ? dispatchDay : {};
  const dispatchDayLabel = formatDispatchDayConfirmationLabel(
    resolvedDispatchDay.dispatch_date,
    resolvedDispatchDay.label || resolvedDispatchDay.dispatch_date,
  );
  const portalRouteCount = Math.max(0, Math.round(Number(resolvedDispatchDay.portal_route_count) || 0));
  const portalDriverName = normalizeInlineText(resolvedDispatchDay.portal_driver_name) || "ce chauffeur";
  const portalTruckName = normalizeInlineText(resolvedDispatchDay.portal_truck_name) || "le camion sélectionné";
  const isSinglePortalRoute = portalRouteCount === 1;

  if (safeAction === "publish_portal") {
    return {
      eyebrow: "Portail chauffeur",
      title: isSinglePortalRoute
        ? `Publier la tournée ${portalTruckName} ?`
        : `Publier ${portalRouteCount || "les"} tournées du ${dispatchDayLabel} ?`,
      description: isSinglePortalRoute
        ? `Pour le ${dispatchDayLabel}, seul ce camion (${portalTruckName}) sera publié dans le portail de ${portalDriverName}. Aucune autre tournée ne sera rendue visible.`
        : "Les chauffeurs liés pourront consulter uniquement la version publiée de leur tournée pour cette période.",
      impactItems: isSinglePortalRoute ? [] : [
        `${portalRouteCount || "Les"} tournées sélectionnées deviendront visibles dans le portail chauffeur.`,
        "Une modification ultérieure du planning retirera automatiquement la tournée concernée du portail.",
        "Les confirmations chauffeur et les livraisons déjà gelées restent conservées.",
      ],
      panelSize: "compact",
      summaryItems: buildPortalPublicationConfirmationSummaryItems(resolvedDispatchDay),
      summaryDensity: "compact",
      summaryLayout: "three",
      confirmLabel: portalRouteCount === 1 ? "Publier cette tournée" : "Publier les tournées",
      cancelLabel: "Annuler",
      tone: "accent",
      warningMessage: isSinglePortalRoute
        ? "Une modification du planning retirera cette tournée du portail. Les confirmations et les livraisons déjà gelées resteront conservées."
        : "L'ancienne version n'est jamais conservée visible après une modification du camion.",
    };
  }

  if (safeAction === "unpublish_portal") {
    return {
      eyebrow: "Portail chauffeur",
      title: isSinglePortalRoute
        ? `Retirer la tournée ${portalTruckName} ?`
        : `Retirer ${portalRouteCount || "les"} tournées du ${dispatchDayLabel} ?`,
      description: isSinglePortalRoute
        ? `Pour le ${dispatchDayLabel}, seul ce camion (${portalTruckName}) sera retiré du portail de ${portalDriverName}. Les autres tournées publiées resteront accessibles.`
        : "Les chauffeurs concernés perdront immédiatement l'accès à ces tournées publiées.",
      impactItems: isSinglePortalRoute ? [] : [
        `${portalRouteCount || "Les"} tournées sélectionnées ne seront plus visibles dans le portail chauffeur.`,
        "Le planning opérateur, les confirmations et les tonnages réels restent inchangés.",
        "Vous pourrez republier plus tard la version courante de chaque tournée.",
      ],
      panelSize: "compact",
      summaryItems: buildPortalPublicationConfirmationSummaryItems(resolvedDispatchDay),
      summaryDensity: "compact",
      summaryLayout: "three",
      confirmLabel: portalRouteCount === 1 ? "Retirer cette tournée" : "Retirer les tournées",
      cancelLabel: "Annuler",
      tone: "warning",
      warningMessage: isSinglePortalRoute
        ? "Le retrait est immédiat. Le planning, les confirmations et les tonnages réels restent inchangés."
        : "Le retrait est immédiat côté chauffeur, sans dégeler les livraisons faites.",
    };
  }

  if (safeAction === "reopen") {
    return {
      eyebrow: "Journée dispatch",
      title: `Confirmer la réouverture du ${dispatchDayLabel} ?`,
      description: "Cette action retire uniquement l'état Fermée dans le calendrier. Elle ne supprime ni ne recalcule les affectations déjà visibles.",
      impactItems: [
        "L'indicateur couleur Fermée disparaîtra du calendrier de dispatch.",
        "Les réaffectations, approbations et autres mutations manuelles seront de nouveau autorisées.",
        "Le planning visible reste inchangé tant qu'aucune autre action n'est lancée.",
      ],
      summaryItems: buildDispatchDayConfirmationSummaryItems(resolvedDispatchDay),
      confirmLabel: "Rouvrir la journée",
      cancelLabel: "Annuler",
      tone: "accent",
      warningMessage: "Aucune donnée opérationnelle n'est supprimée lors de la réouverture.",
    };
  }

  if (safeAction === "invalidate") {
    return {
      eyebrow: "Dispatch admin",
      title: `Confirmer l'invalidation du ${dispatchDayLabel} ?`,
      description: "Cette action marque le dispatch calculé comme obsolète sans supprimer les tournées visibles. Le « ! » réapparaîtra aussitôt pour cette journée.",
      impactItems: [
        "La journée passera en état À revalider dans le calendrier de dispatch.",
        "Les exports du snapshot courant seront signalés comme obsolètes jusqu'à une nouvelle optimisation.",
        "Les tournées visibles restent consultables tant qu'aucun nouveau calcul n'est lancé.",
      ],
      summaryItems: buildDispatchDayConfirmationSummaryItems(resolvedDispatchDay),
      confirmLabel: "Invalider le dispatch",
      cancelLabel: "Annuler",
      tone: "warning",
      warningMessage: "Aucun arrêt ni aucune affectation n'est supprimé: seul le statut opérationnel du snapshot est invalidé.",
    };
  }

  return {
    eyebrow: "Journée dispatch",
    title: `Confirmer la fermeture du ${dispatchDayLabel} ?`,
    description: "Cette action ne modifie pas les affectations déjà visibles. Elle sert surtout à afficher la journée comme fermée dans le calendrier.",
    impactItems: [
      "La journée affichera l'indicateur couleur Fermée dans le calendrier de dispatch.",
      "Les réaffectations, approbations et autres mutations manuelles seront bloquées tant qu'elle restera fermée.",
      "Un clic sur Rouvrir la journée retirera simplement cet indicateur et réautorisera les mutations.",
    ],
    summaryItems: buildDispatchDayConfirmationSummaryItems(resolvedDispatchDay),
    confirmLabel: "Clore la journée",
    cancelLabel: "Annuler",
    tone: "warning",
    warningMessage: "Aucune commande, tournée ou affectation existante n'est supprimée par cette action.",
  };
}
